<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, reactive, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import ButtonDaisy from '@/components/common/buttons/ButtonDaisy.vue'
import OrderActionsCommercial from '@/components/features/order/OrderActionsCommercial.vue'
import OrderActionsAdv from '@/components/features/order/OrderActionsAdv.vue'
import TextInput from '@/components/common/inputs/TextInput.vue'
import DateInput from '@/components/common/inputs/DateInput.vue'
import WysiwygInput from '@/components/common/inputs/WysiwygInput.vue'
import DynamicForm from '@/components/features/forms/DynamicForm.vue'
import OrderFileInput from '@/components/common/inputs/OrderFileInput.vue'
import {
  MagnifyingGlassIcon,
  ArrowPathIcon,
  BanknotesIcon,
  CalendarDaysIcon,
  ClockIcon,
  CreditCardIcon,
  BuildingOffice2Icon,
  DocumentTextIcon,
  UserCircleIcon,
  EnvelopeIcon,
  MapPinIcon,
  UserPlusIcon,
  ChartBarIcon,
  InformationCircleIcon,
  DocumentDuplicateIcon,
  CheckCircleIcon,
  ComputerDesktopIcon,
  PhoneIcon,
  SignalIcon,
  ArrowTopRightOnSquareIcon,
  LinkIcon,
  PencilSquareIcon,
  ArrowDownTrayIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
  PaperAirplaneIcon,
} from '@heroicons/vue/24/outline'
import ConfirmModal from '@/components/common/layout/ConfirmModal.vue'
import OrderChildrenModal from '@/components/features/order/OrderChildrenModal.vue'
import OperatorCommentBlock from '@/components/features/order/OperatorCommentBlock.vue'
import DerogationDetailModal from '@/components/features/derogations/DerogationDetailModal.vue'
import type { DynamicFormSchema, FormField, FormSection } from '@/types/form'
import { useOrderStore } from '@/stores/useOrderStore'
import { useQuoteStore } from '@/stores/useQuoteStore'
import { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'
import { useSuiteStore } from '@/stores/useSuiteStore'
import { useToast } from 'vue-toast-notification'
import { ORDER_STATUS } from '@/constants/orderStatus'
import { formatDate, formatTimeAgo } from '@/utils/dateUtils'
import { generateFormPdf } from '@/utils/formToPdfHtml'
import { mapConditionalDisplayToCondition } from '@/utils/formConditionUtils'
import { useOrderDerogationStore } from '@/stores/useOrderDerogationStore'
import { useCompanyArbitrationStore } from '@/stores/useCompanyArbitrationStore'
import { useMyTeamStore } from '@/stores/useMyTeamStore'
import ArbitrationDetailModal from '@/components/features/arbitrations/ArbitrationDetailModal.vue'
import HelpButton from '@/components/common/help/HelpButton.vue'
const route = useRoute()
const router = useRouter()
const orderStore = useOrderStore()
const orderDerogationStore = useOrderDerogationStore()
const arbitrationStore = useCompanyArbitrationStore()
const quoteStore = useQuoteStore()
const boostCoreStore = useBoostCorePermissionStore()
const myTeamStore = useMyTeamStore()
const suiteStore = useSuiteStore()

const $toast = useToast()

const orderUuid = computed(() => route.params.orderUuid as string | undefined)
const isEditMode = computed(() => !!orderUuid.value)

const missingDerogations = computed(() => orderDerogationStore.currentDerogation?.missing ?? [])
const pendingDerogations = computed(() => orderDerogationStore.currentDerogation?.pending ?? [])
const approvedDerogations = computed(() => orderDerogationStore.currentDerogation?.approved ?? [])
const rejectedDerogations = computed(() => orderDerogationStore.currentDerogation?.rejected ?? [])

const hasMissingDerogations = computed(() => missingDerogations.value.length > 0)
const hasPendingDerogations = computed(() => pendingDerogations.value.length > 0)
const hasApprovedDerogations = computed(() => (orderDerogationStore.currentDerogation?.approved ?? []).length > 0)
const hasRejectedDerogations = computed(() => (orderDerogationStore.currentDerogation?.rejected ?? []).length > 0)

const getDerogationKey = (derogation: any) => `${derogation.type}-${derogation.level ?? 'UNKNOWN'}`

const orderStatus = computed(() => {
  const order = orderStore.currentOrder
  return order?.orderStatus ?? order?.order_status ?? order?.status ?? (order?.draft === true ? ORDER_STATUS.DRAFT : (!order ? ORDER_STATUS.DRAFT : null))
})

const isWaiting = computed(() => {
  const s = String(orderStatus.value || '').toUpperCase()
  return s === ORDER_STATUS.WAITING || s === 'WAITTING'
})

const isDraftOrder = computed(() => {
  if (!isEditMode.value) return true
  const s = String(orderStatus.value || '').toUpperCase()
  return s === ORDER_STATUS.DRAFT || s === 'BROUILLON'
})

// Affiche la carte Dérogation si des dérogations existent réellement (approuvées /
// en attente / rejetées), ou — uniquement en brouillon — s'il en manque encore.
// On ne se base PAS sur `required` (re-détecté à chaque check) : sur une commande
// déjà envoyée, c'est trop tard pour re-détecter, seul l'existant compte.
const hasAnyDerogation = computed(() =>
  hasApprovedDerogations.value ||
  hasPendingDerogations.value ||
  hasRejectedDerogations.value ||
  (isDraftOrder.value && hasMissingDerogations.value),
)

// Arbitrage BU sur la société
const arbitrationCheck = computed(() => arbitrationStore.currentArbitration)
const arbitrationNeeded = computed(() => arbitrationCheck.value?.needsArbitration === true)
const arbitrationPending = computed<any[]>(() => arbitrationCheck.value?.pending ?? [])
const arbitrationApproved = computed<any[]>(() => arbitrationCheck.value?.approved ?? [])
const arbitrationRejected = computed<any[]>(() => arbitrationCheck.value?.rejected ?? [])
const hasPendingArbitration = computed(() => arbitrationPending.value.length > 0)
const hasApprovedArbitration = computed(() => arbitrationApproved.value.length > 0)
const hasRejectedArbitration = computed(() => arbitrationRejected.value.length > 0)
const hasAnyArbitration = computed(
  () =>
    arbitrationNeeded.value ||
    hasPendingArbitration.value ||
    hasApprovedArbitration.value ||
    hasRejectedArbitration.value,
)
const canValidateArbitration = computed(
  () => boostCoreStore.include('ARBITRATION_VALIDATE') || boostCoreStore.include('SUPER_ADMIN'),
)

const showModalArbitrationRequest = ref(false)
const arbitrationCommercialArgument = ref('')
const isSubmittingArbitration = ref(false)
const showArbitrationDetailModal = ref(false)
const selectedArbitration = ref<any | null>(null)
const isOpeningArbitration = ref(false)

async function openArbitrationDetail(item: { uuid?: string }) {
  if (!item?.uuid) return
  isOpeningArbitration.value = true
  try {
    const full = await arbitrationStore.fetchArbitration(item.uuid)
    if (!full) {
      $toast.error('Impossible de charger l\'arbitrage')
      return
    }
    selectedArbitration.value = full
    showArbitrationDetailModal.value = true
  } finally {
    isOpeningArbitration.value = false
  }
}

async function onArbitrationDecided() {
  showArbitrationDetailModal.value = false
  if (orderUuid.value) await arbitrationStore.fetchCheckArbitration(orderUuid.value)
}

async function onSubmitArbitrationRequest() {
  if (!orderUuid.value) return
  if (!arbitrationCommercialArgument.value.trim() || arbitrationCommercialArgument.value.trim().length < 10) {
    $toast.error('La justification doit faire au moins 10 caractères')
    return
  }
  isSubmittingArbitration.value = true
  try {
    await arbitrationStore.createArbitration(orderUuid.value, arbitrationCommercialArgument.value.trim())
    $toast.success('Demande d\'arbitrage soumise')
    showModalArbitrationRequest.value = false
    arbitrationCommercialArgument.value = ''
    await arbitrationStore.fetchCheckArbitration(orderUuid.value)
  } catch (error: any) {
    console.error('Error submitting arbitration:', error)
    $toast.error(error?.message || 'Erreur lors de la soumission de l\'arbitrage')
  } finally {
    isSubmittingArbitration.value = false
  }
}

const isBlockedBusiness = computed(() => String(orderStatus.value || '').toUpperCase() === ORDER_STATUS.BLOCKED_BUSINESS)

const isInProgress = computed(() => String(orderStatus.value || '').toUpperCase() === ORDER_STATUS.IN_PROGRESS)
const isSentToBuManager = computed(() => String(orderStatus.value || '').toUpperCase() === ORDER_STATUS.SENT_TO_BU_MANAGER)

const isValidated = computed(() => String(orderStatus.value || '').toUpperCase() === ORDER_STATUS.VALIDATED)
const isCanceled = computed(() => String(orderStatus.value || '').toUpperCase() === ORDER_STATUS.CANCELED)
const isFinalized = computed(() => isValidated.value || isCanceled.value)

const processingDate = computed(() => orderStore.currentOrder?.processingDate || orderStore.currentOrder?.processedAt || null)

const canSalesEdit = computed(
  () => (!isFinalized.value && !isInProgress.value && !isSentToBuManager.value) || isSuperAdmin.value,
)

const isFormReadOnly = computed(() => {
  if (!isEditMode.value) return false
  // Un enfant en "Workflow parent" est piloté par sa commande parente :
  // son formulaire doit rester consultable mais jamais modifiable.
  if (parentalOrderUuid.value && currentOrderFollowsParent.value) return true
  if (isFinalized.value) return true
  if (isSentToBuManager.value && !isSuperAdmin.value) return true
  if (isDraftOrder.value && isAuthorOrCommercial.value) return false
  return (isInProgress.value && !isAdv.value) || (isAdv.value && !isAssignedToMe.value)
})

const statusLabel = computed(() => {
  return orderStore.getStatusLabel(orderStatus.value)
})

const isAdv = computed(() => boostCoreStore.isAdv)
const isAdvManager = computed(() => boostCoreStore.isAdvManager)
const isSuperAdmin = computed(() => boostCoreStore.isSuperAdmin)
const isProjectRead = computed(() => boostCoreStore.isProjectRead)
const isProjectWrite = computed(() => boostCoreStore.isProjectWrite)
const canForceMarge = computed(() => boostCoreStore.can('ADV_WRITE'))
const canWriteOperatorComment = computed(
  () => isProjectWrite.value || boostCoreStore.can('ADV_WRITE') || isSuperAdmin.value,
)

// Validation de dérogation directement depuis la page commande (DV / DG / super admin)
const isDerogDv = computed(() => boostCoreStore.include('DEROGATION_DV'))
const isDerogDg = computed(() => boostCoreStore.include('DEROGATION_DG') || boostCoreStore.include('SUPER_ADMIN'))
function canValidateDerogationLevel(level?: string): boolean {
  if (isDerogDg.value) return true
  return isDerogDv.value && level === 'DV'
}
const showDerogationDetailModal = ref(false)
const selectedDerogation = ref<any | null>(null)
const isOpeningDerogation = ref(false)

async function openDerogationValidation(item: { uuid?: string }) {
  if (!item?.uuid) return
  isOpeningDerogation.value = true
  try {
    const full = await orderDerogationStore.fetchDerogation(item.uuid)
    if (!full) {
      $toast.error('Impossible de charger la dérogation')
      return
    }
    selectedDerogation.value = full
    showDerogationDetailModal.value = true
  } finally {
    isOpeningDerogation.value = false
  }
}

async function onDerogationValidated() {
  showDerogationDetailModal.value = false
  // Recharge la page pour refléter l'état (readyToSend, dérogations approuvées, etc.)
  window.location.reload()
}
const detailCalculate = ref<string>()

// ─── Commentaires opérateur ───────────────────────────────────────────────
const operatorCommentBlockRef = ref<HTMLElement | null>(null)
const operatorComments = computed(() => orderStore.currentOrder?.operatorComments ?? [])
const showOperatorCommentBlock = ref(false)

function openOperatorCommentBlock() {
  showOperatorCommentBlock.value = true
  nextTick(() => {
    operatorCommentBlockRef.value?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  })
}

const orderUuids = computed(() => orderStore.orderUuids)
const currentIndex = computed(() => {
  if (!orderUuid.value || !orderUuids.value.length) return -1
  return orderUuids.value.indexOf(orderUuid.value)
})

const prevOrderUuid = computed(() => {
  if (currentIndex.value > 0) {
    return orderUuids.value[currentIndex.value - 1]
  }
  return null
})

const nextOrderUuid = computed(() => {
  if (currentIndex.value !== -1 && currentIndex.value < orderUuids.value.length - 1) {
    return orderUuids.value[currentIndex.value + 1]
  }
  return null
})

async function navigateToOrder(uuid: string) {
  await router.push(`/dashboard/order/${uuid}`)
}


const selectedQuote = ref<any | null>(null)
type QuoteComputedCommercial = {
  margeCompteur?: number
  margeTotalContrat?: number
  totalCaContratHt?: number
  totalCaHt?: number
  totalCaTtc?: number | null
  amountTax?: number | null
}

const quoteDevisInfos = computed(() => {
  const q = selectedQuote.value
  if (!q) return null
  const cc = q.computedCommercial as QuoteComputedCommercial | undefined
  const useApi =
    cc &&
    typeof cc.margeCompteur === 'number' &&
    typeof cc.margeTotalContrat === 'number' &&
    typeof cc.totalCaContratHt === 'number'
  const marge = Number(q.marge) || 0
  const ca = Number(q.totalCa?.toFixed(2) || 0)
  let margeCompteur: number
  let margeTotal: number
  let totalCA: number
  let totalCaUntaxed: number
  if (useApi && cc) {
    margeCompteur = cc.margeCompteur!
    margeTotal = cc.margeTotalContrat!
    totalCA = cc.totalCaContratHt!
    totalCaUntaxed = cc.totalCaHt!
  } else {
    margeCompteur = marge
    margeTotal = marge
    totalCA = ca
    totalCaUntaxed = ca
  }
  detailCalculate.value = useApi
    ? 'Valeurs depuis l’API (computedCommercial)'
    : `${marge.toFixed(2)} (base devis, computedCommercial absent)`
  return {
    signedAt: q.signedAt ? new Date(q.signedAt).toLocaleDateString('fr-FR') : '-',
    signedPdfUrl: (q.metadata as { signedPdfUrl?: string })?.signedPdfUrl ?? null,
    duration: q.subscriptionDuration?.label || q.subscriptionDurationCode || '-',
    margeTotal,
    totalCA,
    totalCaUntaxed,
    margeCompteur,
    totalCAHint: useApi ? 'HT' : null,
    modePaiement: q.effectivePaymentMode?.type || q.effectivePaymentMode?.code || q.paymentMode?.type || '-',
    orderType: q.orderType || '-',
  }
})

const orderStoredMargeCompteur = computed((): number | null => {
  if (!isEditMode.value) return null
  const o = orderStore.currentOrder as { margeCompteur?: number | null; marge_compteur?: number | null } | null
  const raw = o?.margeCompteur ?? o?.marge_compteur
  if (raw == null) return null
  const n = Number(raw)
  return Number.isFinite(n) ? n : null
})

const effectiveMargeCompteur = computed(() => {
  if (!quoteDevisInfos.value) return null
  const orderForceMarge = isEditMode.value ? (orderStore.currentOrder?.forceMarge ?? null) : null
  if (orderForceMarge != null) return orderForceMarge
  return quoteDevisInfos.value.margeCompteur
})

/** Donnée figée sur la commande (ligne order.marge_compteur), informative si le devis a bougé ou si marge forcée. */
const margeCompteurAtValidationInfo = computed(() => {
  if (!isValidated.value || !isEditMode.value || orderStoredMargeCompteur.value == null) return ''
  const n = orderStoredMargeCompteur.value
  const amount = n.toFixed(2)
  const raw = orderStore.currentOrder?.validatedAt ?? (orderStore.currentOrder as { validated_at?: string })?.validated_at
  if (!raw) return `Marge compteur enregistrée à la validation : ${amount} €`
  const formatted = formatDate(String(raw))
  if (!formatted || formatted === 'false') return `Marge compteur enregistrée à la validation : ${amount} €`
  return `Marge compteur enregistrée à la validation le ${formatted} : ${amount} €`
})

const isMargeForced = computed(() => {
  if (!quoteDevisInfos.value) return false
  if (canForceMarge.value) {
    return forceMarge.value !== '' && Number(forceMarge.value) !== quoteDevisInfos.value.margeCompteur
  }
  return isEditMode.value && orderStore.currentOrder?.forceMarge != null
})

const forceMarge = ref<string>('')
const isManualMarge = ref(false)
const showModalSendAdv = ref(false)
const showMissingFieldBorders = ref(false)
const showModalValidate = ref(false)
const showModalCancel = ref(false)
const showModalDelete = ref(false)
const showModalUnvalidate = ref(false)
const showModalRefuseBuManager = ref(false)
const refuseBuManagerComment = ref('')
const showProcessingDateModal = ref(false)
const newProcessingDate = ref('')
const processingDateComment = ref('')
const showModalSignManually = ref(false)
const signManuallyDate = ref<string>('')
const isSigningManually = ref(false)
const showModalDerogationRequest = ref(false)
const derogationComments = reactive<Record<string, string>>({})
const isSubmittingDerogation = ref(false)
const isSaving = ref(false)
const signedQuoteFile = ref<File | null>(null)

const hasQuoteSignatureManuelle = computed(() => {
  const q = selectedQuote.value
  if (!q) return false
  return !!(
    q.signedManuallyAt ??
    q.signed_manually_at ??
    q.signedManuallyUuid ??
    q.signed_manually_uuid
  )
})
const orderFilesSignedQuote = computed(() => {
  const order = orderStore.currentOrder
  const files = order?.orderFiles ?? order?.order_files ?? []
  if (!files?.length) return []
  return files.filter((f: any) => {
    const fn = f.fieldName ?? f.field_name ?? ''
    return fn === 'devis_signe'
  })
})
const hasSignedQuoteFile = computed(
  () => !!signedQuoteFile.value || orderFilesSignedQuote.value.length > 0,
)

const canSignManually = computed(
  () =>
    (boostCoreStore.can('ADV_WRITE') || (boostCoreStore.can('SALES_READ'))) &&
    !!selectedQuote.value?.uuid &&
    !selectedQuote.value?.signedAt &&
    !isFinalized.value,
)

// Enfants en "Workflow parent" dont le devis n'est pas signé — bloquent l'envoi ADV
const unsignedWorkflowParentChildren = computed(() =>
  selectedChildren.value.filter(
    (c) => c.followParentWorkflow && c.exists && !c.signedAt && !c.signedManuallyAt
  )
)

const canSendToAdv = computed(() => {
  const q = selectedQuote.value
  if (!q) return false
  if (!typeContratCode.value) return false
  const mp = quoteDevisInfos.value?.modePaiement
  if (!mp || mp === '-') return false
  if (!ventilationHasAtLeastOne.value) return false
  if (!q.signedAt) return false
  if (hasQuoteSignatureManuelle.value && !hasSignedQuoteFile.value) return false
  if (unsignedWorkflowParentChildren.value.length > 0) return false
  if (orderDerogationStore.currentDerogation?.readyToSend === false) return false
  if (arbitrationStore.currentArbitration?.readyToSend === false) return false
  // Commentaire obligatoire sauf pour les commerciaux UNIXO_NEGOCE
  if (!boostCoreStore.isNegoce && commentaireAjout.value.trim() === '') return false
  return true
})

const sendAdvBlockReason = computed(() => {
  const q = selectedQuote.value
  if (!q) return null
  const mp = quoteDevisInfos.value?.modePaiement
  const hasModePaiement = !!mp && mp !== '-'
  if (!q.signedAt && !typeContratCode.value && !hasModePaiement && !ventilationHasAtLeastOne.value)
    return 'Devis non signé, type de contrat, mode de paiement et famille manquants'
  if (!q.signedAt) return 'Le devis doit être signé avant envoi ADV'
  if (hasQuoteSignatureManuelle.value && !hasSignedQuoteFile.value)
    return 'Le devis signé manuellement doit être uploadé avant envoi ADV'
  if (!typeContratCode.value) return 'Le type de contrat est obligatoire'
  if (!hasModePaiement) return 'Le mode de paiement est manquant. Modifiez-le sur Odoo puis réactualisez la page.'
  if (!ventilationHasAtLeastOne.value) return 'Une famille doit être déclarée avant envoi ADV'
  if (unsignedWorkflowParentChildren.value.length > 0) {
    const refs = unsignedWorkflowParentChildren.value.map((c) => c.quoteReference).join(', ')
    return `Devis enfant(s) non signé(s) : ${refs}`
  }
  return null
})

const sendAdvRequirements = computed(() => ({
  signed: !!selectedQuote.value?.signedAt,
  typeContrat: !!typeContratCode.value,
  modePaiement: !!(quoteDevisInfos.value?.modePaiement && quoteDevisInfos.value?.modePaiement !== '-'),
  famille: ventilationHasAtLeastOne.value,
  devisSigne: hasQuoteSignatureManuelle.value ? hasSignedQuoteFile.value : undefined,
  enfantsSignes: selectedChildren.value.some((c) => c.followParentWorkflow && c.exists)
    ? unsignedWorkflowParentChildren.value.length === 0
    : undefined,
  derogationReadyToSend: orderDerogationStore.currentDerogation?.readyToSend !== false,
  arbitrationReadyToSend: arbitrationStore.currentArbitration?.readyToSend !== false,
  // Pour UNIXO_NEGOCE le commentaire n'est pas requis → on masque la ligne du checklist
  hasComment: boostCoreStore.isNegoce ? undefined : !!commentaireAjout.value,
}))

const isAssignedToMe = computed(() => {
  const order = orderStore.currentOrder
  if (!order || !boostCoreStore.user) return false
  // On compare l'UUID de l'utilisateur actuel avec celui de l'ADV de la commande
  // Le champ peut varier selon le backend, on check les noms probables
  return (
    order.advUuid === boostCoreStore.user.uuid ||
    order.adv_uuid === boostCoreStore.user.uuid ||
    order.assignedToUuid === boostCoreStore.user.uuid ||
    order.assigned_to_uuid === boostCoreStore.user.uuid
  )
})

const canAdvAction = computed(() => isAdv.value && (isWaiting.value || isAssignedToMe.value))
const canUnvalidate = computed(() => isAdv.value && isValidated.value)

const isAuthorOrCommercial = computed(() => {
  //si je suis dans une creation je suis author ou commercial
  if (!orderStore?.currentOrder) return true
  const order = orderStore.currentOrder
  const userUuid = boostCoreStore.user?.uuid
  if (!userUuid) return false

  // Auteur
  const authorUuid = order?.authorUuid || order?.author_uuid || order?.author?.uuid
  if (authorUuid === userUuid) return true

  // Commercial désigné sur le devis
  const salesEngineerUuid = selectedQuote.value?.salesEngineer?.uuid || selectedQuote.value?.salesEngineerUuid
  if (salesEngineerUuid === userUuid) return true

  return false
})

const isManagerOfOrderCommercial = computed(() => {
  const managed = boostCoreStore.directManagedUserUuids ?? []
  if (!managed.length) return false
  const salesEngineerUuid = selectedQuote.value?.salesEngineer?.uuid || selectedQuote.value?.salesEngineerUuid
  return !!salesEngineerUuid && managed.includes(salesEngineerUuid)
})

const isRecursiveManagerOfOrderCommercial = computed(() => {
  const managed = boostCoreStore.recursiveManagedUserUuids ?? []
  if (!managed.length) return false
  const salesEngineerUuid = selectedQuote.value?.salesEngineer?.uuid || selectedQuote.value?.salesEngineerUuid
  return !!salesEngineerUuid && managed.includes(salesEngineerUuid)
})

const canManagerUnblock = computed(
  () => !isAdv.value && isBlockedBusiness.value && isManagerOfOrderCommercial.value,
)

const isSalesManagerInSameBuAsCommercial = computed(() => {
  if (!boostCoreStore.include('SALES_MANAGER') && !boostCoreStore.include('SALES_DIRECTOR')) return false
  const managerBu = boostCoreStore.user?.bu
  const commercialBu = selectedQuote.value?.salesEngineer?.bu
  return !!managerBu && !!commercialBu && managerBu === commercialBu
})

const canValidateBuManager = computed(() => {
  if (!isSentToBuManager.value) return false
  if (isSuperAdmin.value) return true
  if (!boostCoreStore.include('SALES_MANAGER') && !boostCoreStore.include('SALES_DIRECTOR')) return false
  const commercialUuid = selectedQuote.value?.salesEngineer?.uuid
  const commercialBu = selectedQuote.value?.salesEngineer?.bu
  // Le commercial est directement dans mon équipe récursive
  if (commercialUuid && myTeamStore.members.some((m) => m.uuid === commercialUuid)) return true
  // Un membre de mon équipe est dans la même BU que le commercial (niveau +2)
  if (commercialBu && myTeamStore.members.some((m) => m.bu === commercialBu)) return true
  // Fallback : même BU que le commercial sans lien managerUuid
  return isSalesManagerInSameBuAsCommercial.value
})

const salesManagerValidationInfo = computed(() => {
  const order = orderStore.currentOrder
  const validatedAt = order?.salesValidatedAt
  const validator = order?.salesValidator
  if (!validatedAt || !validator) return null
  const d = new Date(validatedAt)
  return {
    name: `${validator.firstName} ${validator.lastName}`,
    date: d.toLocaleDateString('fr-FR'),
    time: d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
  }
})

const buValidationManagerPrefs = computed(() => {
  return (selectedQuote.value?.salesEngineerBuValidationManagerPrefs ?? null) as { CALimit?: string; OrderType?: string } | null
})

const needsBuManagerValidation = computed(() => {
  const vm = buValidationManagerPrefs.value
  if (!vm) return false
  const totalHT = Number(selectedQuote.value?.totalCaUntaxed ?? 0)
  const orderType = String(selectedQuote.value?.orderType ?? '')
  const caLimit = vm.CALimit != null ? parseFloat(String(vm.CALimit)) : null
  if (caLimit !== null && totalHT >= caLimit) return true
  if (vm.OrderType && orderType === vm.OrderType) return true
  return false
})

const isClaiming = ref(false)
const advUsers = ref<any[]>([])
const isLoadingAdvUsers = ref(false)
const selectedAdvUuid = ref<string | null>(null)
const isSwitchingAdv = ref(false)

async function onValidateBuManager() {
  if (!orderUuid.value) return
  isUpdatingStatus.value = true
  try {
    const { ok, errorMessage } = await orderStore.validateBuManager(orderUuid.value)
    if (ok) {
      $toast.success('Commande validée et envoyée en attente ADV.')
      const updatedOrder = await orderStore.fetchOrder(orderUuid.value)
      if (updatedOrder) await applyLoadedOrder(updatedOrder, orderUuid.value)
    } else {
      $toast.error(errorMessage ?? 'Erreur lors de la validation manager.')
    }
  } catch {
    $toast.error('Erreur lors de la validation manager.')
  } finally {
    isUpdatingStatus.value = false
  }
}

function onClickRefuseBuManager() {
  refuseBuManagerComment.value = ''
  showModalRefuseBuManager.value = true
}

async function onConfirmRefuseBuManager() {
  if (!orderUuid.value) return
  if (!refuseBuManagerComment.value.trim()) {
    $toast.warning('Un commentaire est obligatoire pour refuser la commande.')
    return
  }
  isUpdatingStatus.value = true
  try {
    const { ok, errorMessage } = await orderStore.refuseBuManager(orderUuid.value, refuseBuManagerComment.value.trim())
    if (ok) {
      $toast.success('Commande refusée et renvoyée au commercial.')
      showModalRefuseBuManager.value = false
      const updatedOrder = await orderStore.fetchOrder(orderUuid.value)
      if (updatedOrder) await applyLoadedOrder(updatedOrder, orderUuid.value)
    } else {
      $toast.error(errorMessage ?? 'Erreur lors du refus de la commande.')
    }
  } catch {
    $toast.error('Erreur lors du refus de la commande.')
  } finally {
    isUpdatingStatus.value = false
  }
}

async function onAdvChange(newAdvUuid: string) {
  if (!orderUuid.value || !newAdvUuid) return

  isSwitchingAdv.value = true
  try {
    const success = await orderStore.switchAdv(orderUuid.value, newAdvUuid)
    if (success) {
      $toast.success('L\'ADV assigné a été mis à jour avec succès.')
    } else {
      $toast.error('Erreur lors de la mise à jour de l\'ADV assigné.')
    }
  } catch (error) {
    console.error('Erreur lors du changement d\'ADV:', error)
    $toast.error('Erreur lors de la mise à jour de l\'ADV assigné.')
  } finally {
    isSwitchingAdv.value = false
  }
}

watch(
  () => orderStore.currentOrder,
  (newOrder) => {
    if (newOrder) {
      selectedAdvUuid.value =
        newOrder.advUuid ||
        newOrder.adv_uuid ||
        newOrder.assignedToUuid ||
        newOrder.assigned_to_uuid ||
        newOrder.adv?.uuid ||
        newOrder.assignedTo?.uuid ||
        null
    }
  },
  { immediate: true },
)

async function fetchAdvUsersList() {
  isLoadingAdvUsers.value = true
  try {
    advUsers.value = await orderStore.fetchAdvUsers()
  } finally {
    isLoadingAdvUsers.value = false
  }
}

async function onClaimOrder() {
  if (!orderUuid.value) return
  isClaiming.value = true
  try {
    const success = await orderStore.startProcessing(orderUuid.value)
    if (success) {
      window.location.reload()
    }
  } finally {
    isClaiming.value = false
  }
}

const isUpdatingStatus = ref(false)
async function onUpdateStatus(status: string) {
  if (!orderUuid.value) return
  if (!commentaireAjout.value || commentaireAjout.value.trim() === '') {
    $toast.warning('Un commentaire est obligatoire pour changer le statut de la commande.')
    return
  }
  isUpdatingStatus.value = true
  try {
    const success = await orderStore.changeOrderStatus(orderUuid.value, status, commentaireAjout.value)
    if (success) {
      commentaireAjout.value = ''
      showModalValidate.value = false
      if (!isAdv.value && status === ORDER_STATUS.IN_PROGRESS) {
        router.push('/dashboard/orders')
      } else {
        window.location.reload()
      }
    }
  } finally {
    isUpdatingStatus.value = false
  }
}

function onClickValidated() {
  if (!commentaireAjout.value || commentaireAjout.value.trim() === '') {
    $toast.warning('Un commentaire est obligatoire pour valider la commande.')
    return
  }
  showModalValidate.value = true
}

async function onConfirmValidate() {
  await onUpdateStatus(ORDER_STATUS.VALIDATED)
}

function onClickCancel() {
  if (!commentaireAjout.value || commentaireAjout.value.trim() === '') {
    $toast.warning('Un commentaire est obligatoire pour annuler la commande.')
    return
  }
  showModalCancel.value = true
}

function onClickDelete() {
  showModalDelete.value = true
}

async function onConfirmDelete() {
  if (!orderUuid.value) return
  isUpdatingStatus.value = true
  try {
    const ok = await orderStore.deleteOrder(orderUuid.value)
    if (ok) {
      const isCanceled = orderStore.currentOrder?.status?.toUpperCase() === ORDER_STATUS.CANCELED
      $toast.success(isCanceled ? 'La commande a été supprimée' : 'Le brouillon a été supprimé')
      await router.push('/dashboard/orders')
    } else {
      const isCanceled = orderStore.currentOrder?.status?.toUpperCase() === ORDER_STATUS.CANCELED
      $toast.error(isCanceled ? 'Erreur lors de la suppression de la commande' : 'Erreur lors de la suppression du brouillon')
    }
  } catch (err) {
    console.error(err)
    const isCanceled = orderStore.currentOrder?.status?.toUpperCase() === ORDER_STATUS.CANCELED
    $toast.error(isCanceled ? 'Erreur lors de la suppression de la commande' : 'Erreur lors de la suppression du brouillon')
  } finally {
    isUpdatingStatus.value = false
    showModalDelete.value = false
  }
}

function onClickUnvalidate() {
  if (!commentaireAjout.value || commentaireAjout.value.trim() === '') {
    $toast.warning('Un commentaire est obligatoire pour dévalider la commande.')
    return
  }
  showModalUnvalidate.value = true
}

async function onConfirmUnvalidate() {
  showModalUnvalidate.value = false
  await onUpdateStatus(ORDER_STATUS.IN_PROGRESS)
}

async function onConfirmCancel() {
  showModalCancel.value = false
  await onUpdateStatus(ORDER_STATUS.CANCELED)
}

watch(
  () => processingDate.value,
  (val) => {
    if (val) {
      newProcessingDate.value = val.split('T')[0]
    }
  },
  { immediate: true },
)

async function onUpdateProcessingDate() {
  if (!orderUuid.value || !newProcessingDate.value || !processingDateComment.value) return
  isUpdatingStatus.value = true
  try {
    const success = await orderStore.changeOrderDateValidatedAt(orderUuid.value, {
      validatedAt: newProcessingDate.value,
      comment: processingDateComment.value,
    })
    if (success.ok) {
      $toast.success('Date de traitement mise à jour avec succès')
      showProcessingDateModal.value = false
      window.location.reload()
    } else {
      $toast.error(success.errorMessage || 'Erreur lors de la mise à jour de la date de traitement')
    }
  } finally {
    isUpdatingStatus.value = false
  }
}

const searchQuery = ref('')
const isSearching = ref(false)
const hasSearched = ref(false)
let searchDebounceTimer: ReturnType<typeof setTimeout> | null = null

function onSearchQueryInput() {
  if (selectedQuote.value) return
  if (searchDebounceTimer) clearTimeout(searchDebounceTimer)
  if (!searchQuery.value.trim()) return
  searchDebounceTimer = setTimeout(() => onSearch(), 600)
}

const selectedFormType = ref<string | null>(null)
const dynamicFormRef = ref<any>(null)
const isLoadingOrder = ref(false)
const isResyncingQuote = ref(false)

const orderFiles = computed(() => {
  const order = orderStore.currentOrder
  if (!order?.orderFiles?.length) return []
  return order.orderFiles.map((f: any) => ({
    uuid: f.uuid,
    fieldName: f.fieldName,
    fileName: f.fileName,
    path: f.path,
    orderIndex: f.orderIndex ?? 0,
  }))
})

const orderComments = computed(() => {
  const order = orderStore.currentOrder
  return order?.orderComments ?? order?.order_comments ?? []
})

const simulateCommission = computed(() =>
  isEditMode.value ? (orderStore.currentOrder?.simulateCommission ?? null) : (orderStore.quoteSimulateCommission ?? null),
)

const isRefreshingSimulation = ref(false)

const simulatedMargeCompteur = computed(() => {
  const s = simulateCommission.value
  if (!s?.margeCompteur) return null
  return forceMarge.value !== '' ? Number(forceMarge.value) : s.margeCompteur
})

const simulatedCommissionAmount = computed(() => {
  const s = simulateCommission.value
  if (!simulatedMargeCompteur.value || s?.reachedRate == null) return null
  const coef = s.coefficients
  const paymentCoef = coef?.paymentCoef ?? 1
  const durationCoef = coef?.durationCoef ?? 1
  const contractCoef = coef?.contractCoef ?? 1
  const iavCoef = coef?.iavCoef ?? 1
  const salesEngineerShare = s.salesEngineerShare ?? 1
  return simulatedMargeCompteur.value * salesEngineerShare * s.reachedRate * paymentCoef * durationCoef * contractCoef * iavCoef
})

const formLogoIconMap: Record<string, any> = {
  'computer-desktop': ComputerDesktopIcon,
  phone: PhoneIcon,
  signal: SignalIcon,
  'building-office': BuildingOffice2Icon,
}

function resolveLogoIcon(logo: string | undefined): any {
  if (!logo) return null
  return formLogoIconMap[logo] ?? null
}

const formTypeOptions = computed(() => {
  return orderStore.orderModels.map((model: any) => {
    const formId = model.formId || model.template?.formId
    const name = model.template?.name || model.template?.title || model.name || model.title || formId
    const logo = model.template?.logo ?? model.logo
    return {
      id: String(model.id || formId),
      value: formId,
      label: name,
      logoIcon: resolveLogoIcon(logo),
    }
  })
})

const selectedLeadUuid = ref<string | null>(null)
const companyLeads = ref<any[]>([])
const isLoadingLeads = ref(false)
const showModalExistingOrder = ref(false)
const existingOrder = ref<any | null>(null)

// Parentalité (Enfants)
const selectedChildren = ref<any[]>([])
// Snapshot des liaisons telles qu'elles existent en base au moment du chargement.
// Sert à détecter les modifications "en attente" (ajout / retrait / changement de liaison)
// jusqu'à ce que l'utilisateur clique sur Enregistrer sur la commande parente.
const initialChildrenSnapshot = ref<any[]>([])
const showChildModal = ref(false)
const parentalOrderUuid = ref<string | null>(null)
const parentalOrderData = ref<{ uuid: string; quoteReference: string } | null>(null)
const isChildOrder = computed(() => !!parentalOrderUuid.value)
// Quand la commande courante est un enfant qui suit le workflow de son parent,
// son statut est propagé automatiquement → on masque les boutons Envoyer/Renvoyer ADV.
const currentOrderFollowsParent = ref<boolean>(false)
const isChildFollowingParent = computed(() => isChildOrder.value && currentOrderFollowsParent.value)

const hasChild = computed(() => selectedChildren.value.length > 0)

// Détection des modifications "en attente" sur les commandes liées : tant que la commande parente n'a pas
// été enregistrée, les ajouts/retraits/changements de liaison ne sont pas persistés en base.
const childKey = (c: any) => c.orderUuid || `q:${c.quoteReference}`
const childrenHavePendingChanges = computed(() => {
  const snapshot = initialChildrenSnapshot.value
  const current = selectedChildren.value
  if (snapshot.length !== current.length) return true
  const snapshotMap = new Map(snapshot.map((c) => [childKey(c), c]))
  for (const c of current) {
    const orig = snapshotMap.get(childKey(c))
    if (!orig) return true
    if (orig.followParentWorkflow !== c.followParentWorkflow) return true
  }
  return false
})

const canManageChildren = computed(() => {
  if (isChildOrder.value) return false
  if (!isEditMode.value) return !!selectedQuote.value
  return isDraftOrder.value
})

const autoLinkedLead = ref<any | null>(null)
const isLeadLocked = computed(() => !!autoLinkedLead.value)

// Lead actuellement affiché dans la carte (auto-rattaché en priorité, sinon sélection manuelle / commande en cours)
const displayedLead = computed(
  () => autoLinkedLead.value || companyLeads.value.find((l: any) => l.uuid === selectedLeadUuid.value) || orderStore.currentOrder?.lead || null,
)

const LEAD_STATUS_LABELS: Record<string, string> = {
  WAITING: 'En attente',
  ACCEPTED: 'Accepté',
  REFUSED: 'Refusé',
  WON: 'Gagné',
  LOST: 'Perdu',
}
function leadStatusLabel(status?: string | null) {
  return status ? LEAD_STATUS_LABELS[status] || status : ''
}

const hasLead = computed(() => !!selectedLeadUuid.value)
const canSelectLead = computed(() => !isLeadLocked.value)
const quoteUuid = computed(() => selectedQuote.value?.uuid ?? null)

// Rattachement automatique du lead (création) : dès qu'un devis est remonté et une famille choisie,
// on résout côté backend le lead auto-rattaché. S'il existe, la section est verrouillée (non modifiable).
// Sinon, on retombe sur la sélection manuelle.
async function resolveAutoLinkedLead() {
  if (isEditMode.value) return
  const quoteRef = selectedQuote.value?.quoteReference
  const familyCode = selectedFamilyCode.value?.trim()
  const wasAuto = isLeadLocked.value
  autoLinkedLead.value = null
  if (!quoteRef || !familyCode) {
    if (wasAuto) selectedLeadUuid.value = null
    return
  }
  const lead = await orderStore.fetchAutoLinkedLead(quoteRef, familyCode)
  if (lead?.uuid) {

    autoLinkedLead.value = lead
    selectedLeadUuid.value = lead.uuid
  } else if (wasAuto) {
    selectedLeadUuid.value = null
  }
}

watch(
  () => selectedQuote.value?.company?.uuid,
  async (companyUuid) => {
    companyLeads.value = []
    if (!companyUuid) return
    isLoadingLeads.value = true
    companyLeads.value = await orderStore.fetchLeadsByCompany(companyUuid)
    isLoadingLeads.value = false
  },
)

watch(
  () => orderStore.currentOrder?.lead?.uuid,
  (leadUuid) => {
    if (orderUuid.value && leadUuid !== undefined) selectedLeadUuid.value = leadUuid ?? null
  },
)

function stripFileRefs(obj: any): any {
  if (!obj || typeof obj !== 'object') return obj
  if (obj.__file === true) return null
  if (Array.isArray(obj)) return obj.map(stripFileRefs)
  return Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, stripFileRefs(v)]))
}

onMounted(async () => {

  if (isAdv.value) {
    await fetchAdvUsersList()
  }
  const promises: Promise<any>[] = [
    orderStore.ensureOrderModels(),
    orderStore.ensureVentilationFamilies(),
    quoteStore.fetchContractTypes(),
  ]

  // S'assurer qu'on a l'utilisateur actuel pour isAssignedToMe
  // On attend fetchMe() séparément afin que les permissions soient disponibles
  // avant de décider si on doit charger l'équipe
  if (!boostCoreStore.user) {
    try {
      await boostCoreStore.fetchMe()
    } catch (e) {
      console.error('[onMounted] fetchMe failed', e)
    }
  }

  // Charger l'équipe récursive si manager ou directeur des ventes (pour canValidateBuManager)
  // On ignore isLoaded pour toujours avoir des données fraîches à l'ouverture d'une commande
  if (!myTeamStore.isLoading &&
      (boostCoreStore.include('SALES_MANAGER') || boostCoreStore.include('SALES_DIRECTOR'))) {
    promises.push(myTeamStore.fetch())
  }

  if(orderUuid.value) promises.push(orderDerogationStore.fetchCheckDerogation(orderUuid.value))
  if(orderUuid.value) promises.push(arbitrationStore.fetchCheckArbitration(orderUuid.value))

  await Promise.all(promises)
})

const rawJsonSchema = ref<any | null>(null)
const adaptedSchema = computed<DynamicFormSchema | null>(() => {
  if (!rawJsonSchema.value) return null
  return adaptJsonToDynamicFormSchema(rawJsonSchema.value)
})

const formData = ref<Record<string, any>>({})

const isAioForm = computed(
  () => (rawJsonSchema.value?.formId ?? rawJsonSchema.value?.template?.formId) === 'order_unixo_aio',
)

const showMargeInput = computed(() => canForceMarge.value || isAioForm.value)

const aioCalculatedMarge = computed((): number | null => {
  if (!isAioForm.value) return null
  const offer = formData.value.offer_aio as string | undefined
  if (!offer) return null

  const base: Record<string, number> = { aio5: 2500, aio10: 3500, aio15: 4500 }
  const serenityBonus: Record<string, number> = { aio5: 5000, aio10: 6000, aio15: 7000 }
  const bundle = formData.value.bundle_aio as string | undefined
  const discount = formData.value.offer_discount as string | undefined

  const hasSerenity =
    bundle === 'bundle_serenity_with_regie' ||
    bundle === 'bundle_serenity_without_regie' ||
    bundle === 'bundle_serenity_security_with_regie' ||
    bundle === 'bundle_serenity_security_without_regie'
  const hasSecurity =
    bundle === 'bundle_security' ||
    bundle === 'bundle_serenity_security_with_regie' ||
    bundle === 'bundle_serenity_security_without_regie'
  const hasRegie =
    bundle === 'bundle_serenity_with_regie' ||
    bundle === 'bundle_serenity_security_with_regie'

  let marge = base[offer] ?? 0
  if (hasSerenity) marge += serenityBonus[offer] ?? 0
  if (hasSecurity) marge += 1500
  if (discount === 'discount_1') marge -= 500
  if (discount === 'discount_2') marge -= 500

  if (hasRegie) marge += 500

  return marge
})


const commentaireAjout = ref('')
const typeContratCode = ref<string | null>(null)
const selectedFamilyCode = ref<string>('')

const ventilationFamilies = computed(() => orderStore.ventilationFamilies)
const ventilationHasAtLeastOne = computed(() => !!selectedFamilyCode.value?.trim())

// Rattachement auto du lead : déclenché par le devis ou la famille sélectionnée.
watch([() => selectedQuote.value?.quoteReference, selectedFamilyCode], resolveAutoLinkedLead)

type SiteData = { name?: string; street?: string; street2?: string | false; zip?: string; city?: string; country_id?: [number, string] }

function formatSite(site: SiteData | null | undefined): { name: string | null; address: string } {
  if (!site || typeof site !== 'object') return { name: null, address: '' }
  const street2 = typeof site.street2 === 'string' ? site.street2 : null
  const parts = [
    site.street,
    street2,
    [site.zip, site.city].filter(Boolean).join(' '),
    Array.isArray(site.country_id) ? site.country_id[1] : null,
  ].filter(Boolean)
  return {
    name: site.name || null,
    address: parts.length ? parts.join(', ') : '',
  }
}

const siteFacturationDisplay = computed(() => {
  const m = selectedQuote.value?.metadata as { siteFacturation?: SiteData } | null | undefined
  return formatSite(m?.siteFacturation)
})

const siteLivraisonDisplay = computed(() => {
  const m = selectedQuote.value?.metadata as { siteLivraison?: SiteData } | null | undefined
  return formatSite(m?.siteLivraison)
})

const commercialDisplay = computed(() => {
  const se = selectedQuote.value?.salesEngineer
  if (!se) return 'Information non saisie'
  const name = [se.firstName, se.lastName].filter(Boolean).join(' ')
  return name || se.email || 'Information non saisie'
})

const iavDisplay = computed(() => {
  const iav = selectedQuote.value?.iav
  if (!iav) return null
  const name = [iav.firstName, iav.lastName].filter(Boolean).join(' ')
  return name || iav.email || null
})

const advAssigneDisplay = computed(() => {
  const order = orderStore.currentOrder
  const adv = order?.adv || order?.assignedTo
  if (!adv) return null
  const name = [adv.firstName, adv.lastName].filter(Boolean).join(' ')
  return name || adv.email || null
})

const contractTypeOptions = computed(() =>
  (quoteStore.contractTypes || []).map((t: { code: string; label: string }, i: number) => ({
    id: i,
    value: t.code,
    label: t.label,
  })),
)

const typeContratCodeModel = computed({
  get: () => typeContratCode.value ?? '',
  set: (v: string) => {
    typeContratCode.value = v === '' ? null : v
  },
})

async function onSearch() {
  if (!searchQuery.value) return
  searchQuery.value = searchQuery.value.toUpperCase().trim()

  isSearching.value = true
  hasSearched.value = false
  selectedQuote.value = null
  selectedFormType.value = null

  try {
    const response = await quoteStore.fetchQuote(searchQuery.value)
    if (response) {
      const data = response.data || response
      const quote = Array.isArray(data) ? data[0] : data
      if (quote) {
        selectQuote(quote)
      }
    }
  } catch (error) {
    console.error('Erreur lors de la recherche du devis:', error)
  } finally {
    isSearching.value = false
    hasSearched.value = true
  }
}

async function selectQuote(quote: any) {
  selectedQuote.value = quote
  searchQuery.value = quote.quoteReference
  typeContratCode.value = quote.typeContratCode ?? null

  if (!isEditMode.value) {
    selectedChildren.value = []
    parentalOrderUuid.value = null
    orderStore.fetchSimulateCommissionFromQuote(quote.quoteReference)
  }

  // Vérifier si un order existe déjà pour ce quote
  if (quote.order && !isEditMode.value) {
    existingOrder.value = quote.order
    showModalExistingOrder.value = true
  }
}

// --- Fraîcheur du devis : "mis à jour il y a X" + resynchro légère ---
const isReloadingQuote = ref(false)
// Tick léger pour que le "il y a X" reste vivant sans recharger le devis.
const nowTick = ref(Date.now())
let nowTickTimer: ReturnType<typeof setInterval> | null = null
onMounted(() => {
  nowTickTimer = setInterval(() => {
    nowTick.value = Date.now()
  }, 30000)
})
onBeforeUnmount(() => {
  if (nowTickTimer) clearInterval(nowTickTimer)
  nowTickTimer = null
})

/** Dernière modif côté Odoo (write_date == sourceUpdatedAt) — info métier principale. */
const quoteUpdatedAgo = computed(() => formatTimeAgo(selectedQuote.value?.sourceUpdatedAt, nowTick.value))
/** Dernière synchro locale (boost-core) — affichée en complément (tooltip). */
const quoteSyncedAgo = computed(() => formatTimeAgo(selectedQuote.value?.synchedAt, nowTick.value))

/**
 * Re-fetch du devis sélectionné. Le GET /quote/:ref déclenche désormais une re-synchro Odoo
 * (smart skip via write_date) côté back sur la source de l'user connecté ; ce bouton suffit donc
 * à rafraîchir même avant d'avoir enregistré une commande. Le bouton "Forcer sync Odoo"
 * (upsert forcé + reload order) reste réservé au mode édition.
 */
async function reloadSelectedQuote() {
  const ref = selectedQuote.value?.quoteReference?.trim()
  if (!ref) return
  isReloadingQuote.value = true
  try {
    const response = await quoteStore.fetchQuote(ref)
    const data = response?.data ?? response
    const quote = Array.isArray(data) ? data[0] : data
    if (quote) {
      selectedQuote.value = quote
      typeContratCode.value = quote.typeContratCode ?? null
      nowTick.value = Date.now()
      $toast.success('Devis resynchronisé depuis Odoo')
    } else {
      $toast.error('Devis introuvable lors de la resynchro')
    }
  } catch {
    $toast.error('Erreur lors de la resynchro du devis')
  } finally {
    isReloadingQuote.value = false
  }
}

function closeModalExistingOrder() {
  showModalExistingOrder.value = false
  existingOrder.value = null
  searchQuery.value = ''
  selectedQuote.value = null
}

function goToExistingOrder() {
  if (existingOrder.value?.uuid) {
    window.open(`/dashboard/order/${existingOrder.value.uuid}`, '_blank')
  }
  closeModalExistingOrder()
}

function onClickSendAdv() {
  const hasUnsavedChildren = selectedChildren.value.some((c) => !c.exists)
  if (hasUnsavedChildren) {
    $toast.error('Veuillez enregistrer la commande avant d\'envoyer à l\'ADV pour créer les commandes liées.')
    return
  }
  showModalSendAdv.value = true
}

async function onConfirmSendAdv() {
  showModalSendAdv.value = false
  await onSave(false)
  if (!isAdv.value) {
    router.push('/dashboard/orders')
  }
}

async function onSignManuallyConfirm() {
  if (!signManuallyDate.value || !selectedQuote.value?.uuid) return
  isSigningManually.value = true
  try {
    const ok = await quoteStore.signManually(selectedQuote.value.uuid, signManuallyDate.value, orderUuid.value)
    if (ok) {
      $toast.success('Devis signé manuellement avec succès')
      showModalSignManually.value = false
      // Rafraîchir le devis pour mettre à jour signedManuallyAt
      const refreshed = await quoteStore.fetchQuote(selectedQuote.value.quoteReference)
      if (refreshed) {
        const data = refreshed.data || refreshed
        selectedQuote.value = Array.isArray(data) ? data[0] : data
      }
    } else {
      $toast.error('Erreur lors de la signature manuelle')
    }
  } finally {
    isSigningManually.value = false
  }
}

async function onSubmitDerogationRequest() {
  const missing = missingDerogations.value
  if (!missing.length) {
    $toast.error('Aucune dérogation manquante à soumettre')
    return
  }

  const payloads = missing.map((item: any) => {
    const key = getDerogationKey(item)
    const comment = (derogationComments[key] || '').trim()
    return { item, key, comment }
  })

  for (const payload of payloads) {
    if (payload.comment.length < 10) {
      $toast.error(`Argument commercial requis pour : ${payload.item.description || payload.item.type}`)
      return
    }
  }

  isSubmittingDerogation.value = true
  try {
    const requests = payloads.map((payload: { item: any; key: string; comment: string }) => {
      return suiteStore.suiteFetch('order-derogation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          orderUuid: orderUuid.value,
          type: payload.item.type,
          level: payload.item.level,
          commercialArgument: payload.comment,
        }),
      })
    })

    const responses = await Promise.all(requests)
    const failed = responses.some((response) => !response || !response.ok)

    if (failed) {
      $toast.error('Erreur lors de la soumission des demandes de dérogation')
      return
    }

    $toast.success('Demandes de dérogation soumises avec succès')
    showModalDerogationRequest.value = false
    Object.keys(derogationComments).forEach((key) => delete derogationComments[key])

    if (orderUuid.value) {
      await orderDerogationStore.fetchCheckDerogation(orderUuid.value)
    }
  } catch (error) {
    console.error('Error submitting derogation requests:', error)
    $toast.error('Erreur lors de la soumission des demandes de dérogation')
  } finally {
    isSubmittingDerogation.value = false
  }
}

async function onSave(...args: unknown[]) {
  const draft = args[0] as boolean | undefined
  const margeVentilationRecord: Record<string, number> | undefined =
    selectedFamilyCode.value?.trim() ? { [selectedFamilyCode.value]: 0 } : undefined
  const formDataPayload = {
    form_id: selectedFormType.value,
    code: rawJsonSchema.value?.code,
    json: rawJsonSchema.value,
    values: formData.value,
    children: selectedChildren.value,
    ...(margeVentilationRecord && { margeVentilation: margeVentilationRecord }),
  }
  let { files, cleaned } = extractFilesRecursive(formDataPayload)
  if (hasQuoteSignatureManuelle.value && signedQuoteFile.value) {
    files = [...files, { fieldName: 'devis_signe', file: signedQuoteFile.value }]
  }
  const comments = commentaireAjout.value || ''
  const formDataStripped = stripFileRefs(cleaned)
  if (formDataStripped?.values && 'comments' in formDataStripped.values) {
    const { comments: _, ...rest } = formDataStripped.values
    formDataStripped.values = rest
  }
  const formDataClean = removeNullValues(formDataStripped)
  if (formDataClean?.values && 'annexes_section' in formDataClean.values) {
    const { annexes_section: _, ...rest } = formDataClean.values
    formDataClean.values = rest
  }
  if (formDataClean?.values) {
    formDataClean.values = replaceIndexInKeys(formDataClean.values)
  }

  const forceMargeValue = (canForceMarge.value || isAioForm.value) && forceMarge.value !== '' ? Number(forceMarge.value) : undefined
  isSaving.value = true
  const updateDraft = isEditMode.value && (isBlockedBusiness.value || canAdvAction.value) && draft === undefined ? undefined :
    (draft ?? (isDraftOrder.value ? true : undefined))
  const payload = isEditMode.value
    ? buildUpdateOrderPayload(updateDraft, comments, formDataClean, files, typeContratCode.value, selectedLeadUuid.value, forceMargeValue, selectedAdvUuid.value, parentalOrderUuid.value)
    : buildOrderPayload(
        selectedQuote.value?.quoteReference,
        draft ?? true,
        comments,
        formDataClean,
        files,
        typeContratCode.value,
        selectedLeadUuid.value,
        forceMargeValue,
        selectedAdvUuid.value,
        parentalOrderUuid.value
      )
  const result = isEditMode.value ? await orderStore.updateOrder(orderUuid.value!, payload) : await orderStore.createOrder(payload)
  isSaving.value = false
  if (result.ok) {
    const createdOrderUuid = !isEditMode.value ? (result as { ok: boolean; orderUuid?: string }).orderUuid : null
    const toastOpts = { duration: 4000 }
    const isDraftSave = draft === true || (isEditMode.value && (isBlockedBusiness.value || canAdvAction.value) && draft === undefined)
    if (isDraftSave) {
      $toast.success(isBlockedBusiness.value ? 'Modifications enregistrées' : 'Commande enregistrée en brouillon', toastOpts)
      if (createdOrderUuid) {
        router.push(`/dashboard/order/${createdOrderUuid}`)
      } else {
        window.location.reload()
      }
    } else {
      $toast.success(isEditMode.value ? 'Commande mise à jour' : 'Commande enregistrée avec succès', toastOpts)
      if (isEditMode.value) {
        window.location.reload()
      } else {
        const targetUuid = createdOrderUuid
        router.push(targetUuid ? `/dashboard/order/${targetUuid}` : '/dashboard/orders')
      }
    }
  } else {
    const msg = (result as { errorMessage?: string }).errorMessage
    $toast.error(msg ?? `Erreur lors de l'enregistrement${draft === false ? ' de la commande' : ' du brouillon'}`)
  }
}

function onSignedQuoteFileChange(fl: FileList | null) {
  signedQuoteFile.value = fl?.[0] ?? null
}

async function onDeleteFile(uuid: string, context?: { sectionId: string; orderIndex: number }) {
  const ok = await orderStore.deleteOrderFile(uuid)
  if (ok && context?.sectionId && context.orderIndex !== undefined && formData.value[context.sectionId]) {
    formData.value[context.sectionId].splice(context.orderIndex, 1)
  }
  if (!ok) $toast.error('Erreur lors de la suppression du fichier')
}

async function onDownloadZip(sectionId: string, uuids: string[]) {
  await orderStore.downloadZip(uuids)
}

function downloadOrderFilesZip() {
  onDownloadZip(
    '',
    orderFiles.value.map((f: { uuid: string }) => f.uuid),
  )
}

const isDownloadingPdf = ref(false)
const isDownloadingTemplateZip = ref(false)
async function onDownloadTemplateZip() {
  isDownloadingTemplateZip.value = true
  try {
    const ok = await orderStore.downloadTemplateZip()
    if (!ok) $toast.error('Erreur lors du téléchargement des fichiers modèles')
  } finally {
    isDownloadingTemplateZip.value = false
  }
}
function onDownloadPdf() {
  if (!adaptedSchema.value || !formData.value) return
  isDownloadingPdf.value = true
  try {
    const selectedOffers = formData.value.offers ||
      formData.value.offer_telephony_multisite_section
        ?.flatMap((site: Record<string, string>) => Object.values(site))
        .filter(Boolean)
        .map((offer: string) => {
          const siteIndex = offer.indexOf('site')
          return siteIndex !== -1 ? offer.substring(0, siteIndex) : offer
        }) || []

    const offersSection = rawJsonSchema.value.template?.sections
      .flatMap((section: {fields: any}) => section.fields)
      .find((field: {id: string}) => field.id.includes('offers'))
    const options = offersSection?.options

    const offers = options
      ?.filter((option: {value:string}) => selectedOffers.some((offer:string)=> option.value.includes(offer)))
      .map((option: {label:string}) => option.label)
      .join('/') || 'Aucune offre';

    const filename = `EXPORT ${selectedQuote.value?.quoteReference } - ${offers} - ${new Date(selectedQuote.value?.signedAt).toLocaleDateString('fr-FR')}.pdf`

    generateFormPdf(adaptedSchema.value, formData.value, {
      formName: adaptedSchema.value.name,
      quoteRef: selectedQuote.value?.quoteReference,
      companyName: selectedQuote.value?.company?.name,
      createdAt: new Date().toLocaleString('fr-FR'),
      commercial: commercialDisplay.value,
      commercialEmail: selectedQuote.value?.salesEngineer?.email,
      iav: iavDisplay.value ?? undefined,
      iavEmail: selectedQuote.value?.iav?.email,
      totalCA: quoteDevisInfos.value?.totalCA?.toFixed(2),
      margeTotal: quoteDevisInfos.value?.margeTotal?.toFixed(2),
      margeCompteur: quoteDevisInfos.value?.margeCompteur?.toFixed(2),
      signedAt: quoteDevisInfos.value?.signedAt,
      duration: quoteDevisInfos.value?.duration,
      modePaiement: quoteDevisInfos.value?.modePaiement,
      orderType: quoteDevisInfos.value?.orderType,
      siteFacturation:
        siteFacturationDisplay.value?.name || siteFacturationDisplay.value?.address
          ? { name: siteFacturationDisplay.value.name ?? undefined, address: siteFacturationDisplay.value.address }
          : undefined,
      siteLivraison:
        siteLivraisonDisplay.value?.name || siteLivraisonDisplay.value?.address
          ? { name: siteLivraisonDisplay.value.name ?? undefined, address: siteLivraisonDisplay.value.address }
          : undefined,
      orderComments: orderComments.value,
      filename,
    })
  } catch (err) {
    console.error('Erreur génération PDF:', err)
    $toast.error('Erreur lors de la génération du PDF')
  } finally {
    isDownloadingPdf.value = false
  }
}

function goBack() {
  router.push('/dashboard/orders')
}

function goToParentOrder() {
  const parentUuid = parentalOrderData.value?.uuid || parentalOrderUuid.value
  if (parentUuid) {
    router.push(`/dashboard/order/${parentUuid}`)
  }
}

async function onLeadChange(uuid: string | null) {
  selectedLeadUuid.value = uuid
  if (isEditMode.value && orderUuid.value) {
    const res = await orderStore.updateOrder(orderUuid.value, { leadUuid: uuid })
    if (res.ok) {
      orderStore.fetchSimulateCommission(orderUuid.value)
    }
  }
}

async function dissociateLead() {
  await onLeadChange(null)
}

async function refreshSimulation() {
  if (!orderUuid.value) return
  isRefreshingSimulation.value = true
  await orderStore.fetchSimulateCommission(orderUuid.value)
  isRefreshingSimulation.value = false
}

function cleanFormState() {
  if (isEditMode.value) {
    commentaireAjout.value = ''
    selectedQuote.value = null
    selectedFormType.value = null
    rawJsonSchema.value = null
    formData.value = {}
    forceMarge.value = ''
    isManualMarge.value = false
    searchQuery.value = ''
    typeContratCode.value = null
    selectedLeadUuid.value = null
    parentalOrderUuid.value = null
    parentalOrderData.value = null
    currentOrderFollowsParent.value = false
    selectedChildren.value = []
    selectedAdvUuid.value = null
    selectedFamilyCode.value = ''
  } else {
    orderStore.clearCurrentOrder()
    orderDerogationStore.clearCurrentDerogation()
    selectedChildren.value = []
    parentalOrderUuid.value = null
    parentalOrderData.value = null
    currentOrderFollowsParent.value = false
  }
}

function removeNullValues(obj: any): any {
  if (Array.isArray(obj)) {
    return obj.map(removeNullValues).filter((v) => v !== null)
  } else if (obj !== null && typeof obj === 'object' && !(obj instanceof File) && !(obj instanceof FileList)) {
    return Object.fromEntries(
      Object.entries(obj)
        .filter(([_, v]) => v !== null)
        .map(([k, v]) => [k, removeNullValues(v)]),
    )
  }
  return obj
}

function replaceIndexInKeys(obj: any, arrayIndex?: number): any {
  if (obj === null || obj === undefined) return obj
  if (Array.isArray(obj)) {
    return obj.map((item, i) => replaceIndexInKeys(item, i))
  }
  if (typeof obj === 'object' && !(obj instanceof File) && !(obj instanceof FileList)) {
    const result: Record<string, any> = {}
    for (const [key, value] of Object.entries(obj)) {
      const newKey =
        key.includes('{{index}}') && arrayIndex !== undefined ? key.replace(/\{\{index\}\}/g, String(arrayIndex)) : key
      result[newKey] = replaceIndexInKeys(value, undefined)
    }
    return result
  }
  return obj
}

type FileWithField = { fieldName: string; file: File }

function extractFilesRecursive(obj: any): { files: FileWithField[]; cleaned: any } {
  const files: FileWithField[] = []
  function walk(path: string, v: any): any {
    if (v instanceof FileList) {
      const arr = Array.from(v)
      arr.forEach((file) => files.push({ fieldName: path, file }))
      return { __file: true, count: arr.length, names: arr.map((f) => f.name) }
    }
    if (v instanceof File) {
      files.push({ fieldName: path, file: v })
      return { __file: true, name: v.name }
    }
    if (Array.isArray(v)) return v.map((item, i) => walk(`${path}[${i}]`, item))
    if (v !== null && typeof v === 'object') {
      return Object.fromEntries(Object.entries(v).map(([k, v2]) => [k, walk(path ? `${path}.${k}` : k, v2)]))
    }
    return v
  }
  const cleaned = walk('', obj)
  const normalizeFieldName = (path: string) => {
    let s = path.match(/^values\.(.+)$/)?.[1] ?? path
    s = s.replace(/\.(\d+)\./g, '[$1].')
    const idxMatch = s.match(/.*\[(\d+)\].*\{\{index\}\}/)
    if (idxMatch) s = s.replace(/\{\{index\}\}/g, idxMatch[1])
    const annexesMatch = s.match(/annexes_section\[\d+\]\.files_annexe\[(\d+)\]/)
    if (annexesMatch) return `files_annexe[${annexesMatch[1]}]`
    return s
  }
  return {
    files: files.map((f) => ({ ...f, fieldName: normalizeFieldName(f.fieldName) })),
    cleaned,
  }
}

function buildOrderPayload(
  quoteRef: string,
  draft: boolean,
  comment: string,
  formDataPayload: any,
  files: FileWithField[],
  typeContrat?: string | null,
  leadUuid?: string | null,
  forceMargeVal?: number,
  advUuid?: string | null,
  parentalOrderUuid?: string | null,
): FormData | Record<string, any> {
  if (files.length === 0) {
    const payload: Record<string, any> = { quoteRef, draft, comment, formData: formDataPayload }
    if (typeContrat != null) payload.typeContratCode = typeContrat
    if (leadUuid != null) payload.leadUuid = leadUuid
    if (forceMargeVal !== undefined) payload.forceMarge = forceMargeVal
    if (advUuid != null) payload.advUuid = advUuid
    if (parentalOrderUuid != null) payload.parentalOrderUuid = parentalOrderUuid
    return payload
  }
  const fd = new FormData()
  fd.append('quoteRef', quoteRef)
  fd.append('draft', String(draft))
  fd.append('comment', comment)
  fd.append('formData', JSON.stringify(formDataPayload))
  if (typeContrat != null) fd.append('typeContratCode', typeContrat)
  if (leadUuid != null) fd.append('leadUuid', leadUuid)
  if (forceMargeVal !== undefined) fd.append('forceMarge', String(forceMargeVal))
  if (advUuid != null) fd.append('advUuid', advUuid)
  if (parentalOrderUuid != null) fd.append('parentalOrderUuid', parentalOrderUuid)
  files.forEach(({ fieldName, file }) => fd.append(fieldName, file))
  return fd
}

function buildUpdateOrderPayload(
  draft: boolean | undefined,
  comment: string,
  formDataPayload: any,
  files: FileWithField[],
  typeContrat?: string | null,
  leadUuid?: string | null,
  forceMargeVal?: number,
  advUuid?: string | null,
  parentalOrderUuid?: string | null,
): FormData | Record<string, any> {
  if (files.length === 0) {
    const payload: Record<string, any> = { comment, formData: formDataPayload }
    if (draft !== undefined) payload.draft = draft
    if (typeContrat != null) payload.typeContratCode = typeContrat
    if (leadUuid !== undefined) payload.leadUuid = leadUuid
    if (forceMargeVal !== undefined) payload.forceMarge = forceMargeVal
    if (advUuid !== undefined) payload.advUuid = advUuid
    if (parentalOrderUuid !== undefined) payload.parentalOrderUuid = parentalOrderUuid
    return payload
  }
  const fd = new FormData()
  if (draft !== undefined) fd.append('draft', String(draft))
  fd.append('comment', comment)
  fd.append('formData', JSON.stringify(formDataPayload))
  if (typeContrat != null) fd.append('typeContratCode', typeContrat)
  if (leadUuid !== undefined) fd.append('leadUuid', leadUuid === null ? '' : leadUuid)
  if (forceMargeVal !== undefined) fd.append('forceMarge', String(forceMargeVal))
  if (advUuid !== undefined) fd.append('advUuid', advUuid === null ? '' : advUuid)
  if (parentalOrderUuid !== undefined) fd.append('parentalOrderUuid', parentalOrderUuid === null ? '' : parentalOrderUuid)
  files.forEach(({ fieldName, file }) => fd.append(fieldName, file))
  return fd
}

function normalizeFieldType(type: string) {
  if (type === 'checkbox-group') return 'checkbox_group'
  if (type === 'textarea') return 'textarea'
  if (type === 'float') return 'number'
  if (type === 'richtext' || type === 'html') return 'wysiwyg'
  if (type === 'multiselect') return 'select'
  return type
}

function adaptJsonToDynamicFormSchema(json: any): DynamicFormSchema {
  if (!json) return { formId: '', name: '', sections: [] }

  // Si le JSON est enveloppé dans un objet template (format API)
  const actualJson = json.template || json

  const sectionsRaw = actualJson.sections || []

  function adaptSection(sec: any): FormSection {
    const fields: FormField[] = (sec.fields || []).map((f: any) => {
      const field: FormField = {
        id: String(f.id),
        name: f.name,
        label: f.label ?? f.title ?? f.name,
        type: normalizeFieldType(f.type),
        required: !!f.required,
        placeholder: f.placeholder,
        multiple: !!f.multiple || f.type === 'multiselect',
        options: f.options,
        disabled: !!f.disabled || !!f.readonly,
        readonly: !!f.readonly,
        repeatable: f.repeatable,
        repeatableConfig: f.repeatableConfig,
        newRow: !!f.newRow,
      }

      // Si le champ est de type section/under-section, on adapte récursivement ses champs
      if (f.type === 'section' || f.type === 'under-section' || f.repeatable || f.fields) {
        field.fields = (f.fields || []).map((subF: any) => {
          const adaptedSubField: any = {
            ...subF,
            label: subF.label ?? subF.title ?? subF.name,
            type: normalizeFieldType(subF.type),
            disabled: !!subF.disabled || !!subF.readonly,
            readonly: !!subF.readonly,
          }
          const subCond = mapConditionalDisplayToCondition(subF.conditionalDisplay)
          if (subCond) adaptedSubField.condition = subCond
          return adaptedSubField
        })
        if (f.sections) {
          field.sections = f.sections.map((s: any) => adaptSection(s))
        }
      }

      const fCond = mapConditionalDisplayToCondition(f.conditionalDisplay)
      if (fCond) field.condition = fCond

      return field
    })

    const subSections = (sec.sections || []).sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0)).map((s: any) => adaptSection(s))

    const outSection: FormSection = {
      id: String(sec.id),
      name: sec.name || sec.title || sec.label || sec.id,
      fields: fields,
      sections: subSections,
    }

    if (sec.repeatable) {
      outSection.repeatable = true
      outSection.repeatableConfig = sec.repeatableConfig
    }

    const secCond = mapConditionalDisplayToCondition(sec.conditionalDisplay)
    if (secCond) outSection.condition = secCond

    return outSection
  }

  const sectionsSorted = [...sectionsRaw].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
  const outSections: FormSection[] = sectionsSorted.map((sec: any) => adaptSection(sec))

  return {
    formId: actualJson.formId,
    name: actualJson.name || actualJson.title || json.name || json.title || actualJson.formId || '',
    sections: outSections,
  }
}

function buildInitialModelFromJson(json: any, quoteData: any = null) {
  if (!json) return {}
  const actualJson = json.template || json
  const model: Record<string, any> = {}

  const mapping: Record<string, string> = {
    companyName: 'company.name',
    siren: 'company.siren',
    siret: 'company.siret',
    commercial: 'salesEngineer.email',
    meanPayment: 'effectivePaymentMode.type',
  }

  function getValueByPath(obj: any, path: string) {
    return path.split('.').reduce((acc, part) => acc && acc[part], obj)
  }

  function formatFieldName(name: string, index: number): string {
    if (!name?.includes?.('{{index}}')) return name
    return name.replace(/\{\{index\}\}/g, String(index))
  }

  function createRepeatableItem(sec: any, idx: number): Record<string, any> {
    const item: Record<string, any> = {}
    for (const f of sec.fields || []) {
      if (f.type === 'section' || f.type === 'under-section' || f.repeatable || f.fields) {
        if (f.repeatable) {
          item[formatFieldName(f.id || f.name, idx)] = []
        } else {
          for (const subF of f.fields || []) {
            const name = formatFieldName(subF.name, idx)
            item[name] = subF.defaultValue ?? (subF.type === 'switch' ? false : subF.multiple ? [] : null)
          }
        }
      } else {
        const name = formatFieldName(f.name, idx)
        item[name] = f.defaultValue ?? (f.type === 'switch' ? false : f.multiple ? [] : null)
      }
    }
    return item
  }

  function processSection(sec: any) {
    if (sec.repeatable || sec.type === 'section' || sec.type === 'under-section' || sec.fields) {
      if (sec.repeatable) {
        model[sec.id] = []
        const min = sec.repeatableConfig?.min ?? 0
        if (min >= 1 && sec.fields?.length) {
          for (let i = 0; i < min; i++) {
            model[sec.id].push(createRepeatableItem(sec, i))
          }
        }
      } else {
        for (const f of sec.fields || []) {
          processField(f)
        }
      }
    } else {
      for (const f of sec.fields || []) {
        processField(f)
      }
    }

    if (sec.sections && Array.isArray(sec.sections)) {
      sec.sections.forEach((sub: any) => processSection(sub))
    }
  }

  function processField(f: any) {
    if (f.type === 'section' || f.type === 'under-section' || f.repeatable || f.fields) {
      processSection(f)
      return
    }

    // Priorité 1: Données du devis
    const mappedKey = mapping[f.name] || f.name
    const valFromQuote = quoteData ? getValueByPath(quoteData, mappedKey) : undefined

    if (valFromQuote !== undefined) {
      model[f.name] = valFromQuote
    }
    // Priorité 2: Valeur par défaut du JSON
    else if (f.defaultValue !== undefined) {
      model[f.name] = f.defaultValue
    }
    // Priorité 3: Initialisation par type
    else if (f.type === 'switch') {
      model[f.name] = false
    } else if (f.multiple) {
      model[f.name] = []
    } else {
      model[f.name] = null
    }
  }

  for (const sec of actualJson.sections || []) {
    processSection(sec)
  }

  return model
}

watch(
  () => selectedFormType.value,
  async (val) => {
    if (isEditMode.value) return
    cleanFormState()
    if (!val) {
      rawJsonSchema.value = null
      formData.value = {}
      return
    }
    const model = orderStore.orderModels.find((m: any) => (m.formId || m.template?.formId) === val)
    if (model) {
      rawJsonSchema.value = model
      formData.value = buildInitialModelFromJson(rawJsonSchema.value, selectedQuote.value)
    }
  },
)

watch(quoteDevisInfos, (infos) => {
  if (!infos) return
  if (forceMarge.value === '' && !isAioForm.value) {
    forceMarge.value = infos.margeCompteur.toFixed(2)
  }
})

watch(
  [typeContratCode, () => formData.value],
  () => {
    if (formData.value && typeof formData.value === 'object') {
      formData.value.typeContratCode = typeContratCode.value
    }
  },
  { immediate: true },
)

watch(aioCalculatedMarge, (newVal) => {
  if (newVal !== null && !isManualMarge.value) {
    forceMarge.value = String(newVal)
  }
})

watch(isAioForm, (val) => {
  if (!val && quoteDevisInfos.value) {
    forceMarge.value = quoteDevisInfos.value.margeCompteur.toFixed(2)
  }
})

async function applyLoadedOrder(order: any, ctxUuid: string) {
  if (order.forceMarge != null) {
    forceMarge.value = String(order.forceMarge)
    isManualMarge.value = true
  }
  selectedQuote.value = order.quote

  searchQuery.value = order.quote?.quoteReference || ''
  typeContratCode.value = order.quote?.typeContratCode ?? null
  selectedLeadUuid.value = order.lead?.uuid ?? null
  parentalOrderUuid.value = order.parentalOrderUuid || null
  parentalOrderData.value = order.parentalOrder
    ? { uuid: order.parentalOrder.uuid, quoteReference: order.parentalOrder.quote?.quoteReference || order.parentalOrder.uuid }
    : null
  currentOrderFollowsParent.value = order.followParentWorkflow === true
  selectedChildren.value = (order.children || []).map((c: any) => ({
    orderUuid: c.uuid,
    quoteReference: c.quote?.quoteReference || c.quoteReference,
    exists: true,
    followParentWorkflow: c.followParentWorkflow === true,
    signedAt: c.quote?.signedAt ?? null,
    signedManuallyAt: c.quote?.signedManuallyAt ?? null,
  }))
  initialChildrenSnapshot.value = JSON.parse(JSON.stringify(selectedChildren.value))
  selectedAdvUuid.value =
    order.advUuid ||
    order.adv_uuid ||
    order.assignedToUuid ||
    order.assigned_to_uuid ||
    order.adv?.uuid ||
    order.assignedTo?.uuid ||
    null
  const templateFromStore = await orderStore.getOrderTemplateByCode(order.orderTemplateCode)
  rawJsonSchema.value = templateFromStore
  if (!rawJsonSchema.value && order.orderTemplateCode) {
    $toast.warning(`Modèle de commande « ${order.orderTemplateCode} » introuvable dans le catalogue.`)
  }
  selectedFormType.value = rawJsonSchema.value?.template?.formId || null

  const rawFormData = order.formData?.values ?? order.formData ?? {}
  const stripped = stripFileRefs(rawFormData)
  const currentFormData = replaceIndexInKeys(stripped)

  const baseModel = buildInitialModelFromJson(rawJsonSchema.value, order.quote)
  const { annexes_section: _annexes, ...restFormData } = currentFormData
  formData.value = { ...baseModel, ...restFormData, annexes_section: [] }
  await nextTick()
  isManualMarge.value = false

  const meta = order.orderMetadata as { margeVentilation?: Record<string, number> } | null
  const raw = meta?.margeVentilation
  const ventilations = raw && typeof raw === 'object' ? Object.entries(raw) : []
  const firstFamily = ventilations.find(([, v]) => v != null)?.[0] ?? null
  selectedFamilyCode.value = firstFamily || ''

  if (order.quote?.company?.uuid) {
    orderStore.fetchLeadsByCompany(order.quote.company.uuid).then((leads) => {
      companyLeads.value = leads
    })
  }

  if (order.quote?.orderType === 'SUBSCRIPTION') {
    await orderStore.fetchSimulateCommission(ctxUuid)
  }
}

async function resyncQuoteFromOdoo() {
  const ref = selectedQuote.value?.quoteReference?.trim()
  if (!ref) {
    $toast.error('Référence devis manquante')
    return
  }
  const id = orderUuid.value
  if (!id) {
    $toast.error('Commande introuvable')
    return
  }
  const sourceName = selectedQuote.value?.source?.name as string | undefined
  isResyncingQuote.value = true
  try {
    cleanFormState()
    const { ok, errorMessage } = await quoteStore.syncQuote(ref, sourceName)
    if (!ok) {
      $toast.error(errorMessage || 'Échec synchronisation Odoo (save devis)')
      return
    }
    const order = await orderStore.fetchOrder(id)
    if (order) {
      await applyLoadedOrder(order, id)
      $toast.success('Devis synchronisé depuis Odoo et enregistré')
    } else {
      $toast.error('Impossible de recharger la commande')
    }
  } catch {
    $toast.error('Erreur lors de la synchronisation Odoo')
  } finally {
    isResyncingQuote.value = false
  }
}

watch(
  () => orderUuid.value,
  async (newUuid) => {
    isLoadingOrder.value = true
    try {
      cleanFormState()
      if (newUuid) {
        const order = await orderStore.fetchOrder(newUuid)
        if (order) {
          await applyLoadedOrder(order, newUuid)
        }
      }
    } finally {
      isLoadingOrder.value = false
    }
  },
  { immediate: true },
)

watch(
  () => route.path,
  () => {
    if (!orderUuid.value) {
      cleanFormState()
      commentaireAjout.value = ''
      selectedQuote.value = null
      selectedFormType.value = null
      rawJsonSchema.value = null
      formData.value = {}
      forceMarge.value = ''
    isManualMarge.value = false
      searchQuery.value = ''
      typeContratCode.value = null
      selectedLeadUuid.value = null
      selectedAdvUuid.value = null
      selectedFamilyCode.value = ''
    }
  },
)
</script>

<template>
  <div class="min-h-screen bg-base-200/40">
    <div class="px-4 py-6 max-w-[1600px] mx-auto w-full">
      <div class="flex items-start justify-between gap-4">
        <div class="flex items-center gap-4">
          <div>
            <div class="flex items-center gap-1">
              <h1 class="text-2xl font-semibold">{{ isEditMode ? 'Modifier la commande' : 'Créer une commande' }}</h1>
              <HelpButton />
            </div>
            <p class="text-sm text-base-content/70">
              {{ isEditMode ? 'Modifier les informations de la commande.' : 'Recherche un devis, puis complète le formulaire.' }}
            </p>
          </div>

          <div v-if="isEditMode && orderUuids.length > 0" class="flex items-center gap-1">
            <button
              class="btn btn-sm btn-circle btn-ghost"
              :disabled="!prevOrderUuid"
              title="Commande précédente"
              @click="prevOrderUuid && navigateToOrder(prevOrderUuid)"
            >
              <ChevronLeftIcon class="w-5 h-5" />
            </button>
            <span class="text-xs font-medium opacity-60">
              {{ currentIndex + 1 }} / {{ orderUuids.length }}
            </span>
            <button
              class="btn btn-sm btn-circle btn-ghost"
              :disabled="!nextOrderUuid"
              title="Commande suivante"
              @click="nextOrderUuid && navigateToOrder(nextOrderUuid)"
            >
              <ChevronRightIcon class="w-5 h-5" />
            </button>
          </div>
        </div>

        <div class="flex items-center gap-2 shrink-0">

          <ButtonDaisy
            text="Fichiers modèles (ZIP)"
            variant="outline"
            size="sm"
            :icon="ArrowDownTrayIcon"
            :loader="isDownloadingTemplateZip"
            @click="onDownloadTemplateZip"
          />
          <ButtonDaisy
            v-if="selectedQuote && selectedFormType && adaptedSchema"
            text="Télécharger PDF"
            variant="outline"
            size="sm"
            :icon="ArrowDownTrayIcon"
            :loader="isDownloadingPdf"
            @click="onDownloadPdf"
          />
          <ButtonDaisy
            v-if="isEditMode && orderFiles.length > 0"
            text="Télécharger ZIP"
            variant="outline"
            size="sm"
            :icon="ArrowDownTrayIcon"
            @click="downloadOrderFilesZip"
          />
          <button
            v-if="isEditMode && canWriteOperatorComment && !showOperatorCommentBlock && !operatorComments.length"
            type="button"
            class="btn btn-sm gap-2"
            style="background-color: #27E8E8; border-color: #27E8E8; color: #000;"
            @click="openOperatorCommentBlock"
          >
            <PencilSquareIcon class="w-4 h-4" />
            Commentaire Opérateur
          </button>
          <ButtonDaisy text="Retour" variant="ghost" @click="goBack" />
        </div>
      </div>

      <!-- Bloc Commentaires Opérateur (haut — uniquement si lead présent) -->
      <div
        v-if="isEditMode && hasLead && (showOperatorCommentBlock || operatorComments.length > 0)"
        ref="operatorCommentBlockRef"
        class="rounded-lg border-2 border-[#27E8E8]/50 bg-[#27E8E8]/10 mt-4 transition-all"
        style="height: 480px;"
      >
        <OperatorCommentBlock :order-uuid="orderUuid ?? ''" />
      </div>

      <div v-if="hasAnyDerogation" class="card bg-base-100 shadow h-full mt-4">
        <div class="card-body flex flex-col">
          <h2 class="card-title text-lg gap-2">
            <ExclamationTriangleIcon class="w-5 h-5 text-error" />
            Dérogation
          </h2>
          <p class="text-xs opacity-70 flex items-center gap-1 mt-0.5">
            <InformationCircleIcon class="w-3.5 h-3.5 shrink-0" />
            Cette commande fait l'objet de dérogation(s).
          </p>

          <div v-if="hasMissingDerogations && isDraftOrder" class="mt-4 space-y-3 p-4 bg-warning/10 rounded-lg border border-warning/20">
            <div class="flex items-center justify-start gap-3">
              <span class="badge badge-warning badge-sm">{{ missingDerogations.length }}</span>
              <p class="font-semibold">Dérogations manquantes :</p>
            </div>
            <ul class="space-y-2 text-sm list-disc list-inside text-base-content/70">
              <li v-for="item in missingDerogations" :key="getDerogationKey(item)">
                {{ item.description || `${item.type} (${item.level || 'Niveau inconnu'})` }}
              </li>
            </ul>
            <div class="mt-4 flex justify-end">
              <ButtonDaisy
                text="Soumettre une demande"
                variant="primary"
                size="sm"
                :icon="PaperAirplaneIcon"
                @click="showModalDerogationRequest = true"
              />
            </div>
          </div>
          <div v-if="hasPendingDerogations" class="mt-4 space-y-3 p-4 bg-info/10 rounded-lg border border-info/20">
            <div class="flex items-center justify-start gap-3">
              <span class="badge badge-info badge-sm">{{ pendingDerogations.length }}</span>
              <p class="font-semibold">Dérogations en attente</p>
            </div>
            <ul class="space-y-2 text-sm text-base-content/70">
              <li v-for="item in pendingDerogations" :key="item.uuid" class="flex items-center justify-between gap-2">
                <span>{{ item.description || `${item.type} (${item.level || 'Niveau inconnu'})` }}</span>
                <ButtonDaisy
                  v-if="canValidateDerogationLevel(item.level)"
                  text="Valider"
                  variant="primary"
                  size="xs"
                  :loader="isOpeningDerogation"
                  @click="openDerogationValidation(item)"
                />
              </li>
            </ul>
          </div>
          <div v-if="hasApprovedDerogations" class="mt-4 space-y-3 p-4 bg-success/10 rounded-lg border border-info/20">
            <div class="flex items-center justify-start gap-3">
              <span class="badge badge-success badge-sm">{{ approvedDerogations.length }}</span>
              <p class="font-semibold">Dérogations approuvées</p>
            </div>
            <ul class="space-y-2 text-sm list-disc list-inside text-base-content/70">
              <li v-for="item in approvedDerogations" :key="item.uuid">
                {{ item.description || `${item.type} (${item.level || 'Niveau inconnu'})` }}
              </li>
            </ul>
          </div>
          <div v-if="hasRejectedDerogations" class="mt-4 space-y-3 p-4 bg-error/10 rounded-lg border border-error/20">
            <div class="flex items-center justify-start gap-3">
              <span class="badge badge-error badge-sm">{{ rejectedDerogations.length }}</span>
              <p class="font-semibold">Dérogations rejetées</p>
            </div>
            <ul class="space-y-2 text-sm list-disc list-inside text-base-content/70">
              <li v-for="item in rejectedDerogations" :key="item.uuid">
                {{ item.description || `${item.type} (${item.level || 'Niveau inconnu'})` }}
              </li>
            </ul>
          </div>
        </div>
      </div>

      <!-- Carte Arbitrage BU sur la société -->
      <div v-if="hasAnyArbitration" class="card bg-base-100 shadow h-full mt-4">
        <div class="card-body flex flex-col">
          <h2 class="card-title text-lg gap-2">
            <ExclamationTriangleIcon class="w-5 h-5 text-error" />
            Arbitrage BU
          </h2>
          <p class="text-xs opacity-70 flex items-center gap-1 mt-0.5">
            <InformationCircleIcon class="w-3.5 h-3.5 shrink-0" />
            La société sort de votre territoire naturel ; un arbitrage est requis avant envoi ADV.
          </p>

          <div v-if="arbitrationNeeded && isDraftOrder" class="mt-4 space-y-3 p-4 bg-warning/10 rounded-lg border border-warning/20">
            <div class="flex items-center justify-start gap-3">
              <span class="badge badge-warning badge-sm">1</span>
              <p class="font-semibold">Arbitrage requis</p>
            </div>
            <p class="text-sm text-base-content/70">
              Justifie auprès des directeurs commerciaux (BU source et BU cible) pourquoi tu dois conserver ce dossier.
              <span v-if="arbitrationCheck?.decision?.naturalSegment">
                Segment naturel de la société : <strong>{{ arbitrationCheck.decision.naturalSegment }}</strong>.
              </span>
            </p>
            <div class="mt-2 flex justify-end">
              <ButtonDaisy
                text="Soumettre une demande"
                variant="primary"
                size="sm"
                :icon="PaperAirplaneIcon"
                @click="showModalArbitrationRequest = true"
              />
            </div>
          </div>

          <div v-if="hasPendingArbitration" class="mt-4 space-y-3 p-4 bg-info/10 rounded-lg border border-info/20">
            <div class="flex items-center justify-start gap-3">
              <span class="badge badge-info badge-sm">{{ arbitrationPending.length }}</span>
              <p class="font-semibold">Arbitrage en attente</p>
            </div>
            <ul class="space-y-2 text-sm text-base-content/70">
              <li v-for="item in arbitrationPending" :key="item.uuid" class="flex items-center justify-between gap-2">
                <span>En attente de validation par un directeur commercial.</span>
                <ButtonDaisy
                  v-if="canValidateArbitration"
                  text="Valider"
                  variant="primary"
                  size="xs"
                  :loader="isOpeningArbitration"
                  @click="openArbitrationDetail(item)"
                />
                <ButtonDaisy
                  v-else
                  text="Détails"
                  variant="ghost"
                  size="xs"
                  :loader="isOpeningArbitration"
                  @click="openArbitrationDetail(item)"
                />
              </li>
            </ul>
          </div>

          <div v-if="hasApprovedArbitration" class="mt-4 space-y-3 p-4 bg-success/10 rounded-lg border border-info/20">
            <div class="flex items-center justify-start gap-3">
              <span class="badge badge-success badge-sm">{{ arbitrationApproved.length }}</span>
              <p class="font-semibold">Arbitrage approuvé</p>
            </div>
            <p class="text-sm text-base-content/70">
              La société est désormais accessible à toute votre BU sans nouvel arbitrage.
            </p>
          </div>

          <div v-if="hasRejectedArbitration" class="mt-4 space-y-3 p-4 bg-error/10 rounded-lg border border-error/20">
            <div class="flex items-center justify-start gap-3">
              <span class="badge badge-error badge-sm">{{ arbitrationRejected.length }}</span>
              <p class="font-semibold">Arbitrage refusé — commande annulée</p>
            </div>
            <ul class="space-y-2 text-sm list-disc list-inside text-base-content/70">
              <li v-for="item in arbitrationRejected" :key="item.uuid">
                {{ item.decisionComment || 'Sans motif renseigné' }}
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div class="mt-6 space-y-6">
        <!-- Ligne 1: Devis + Client -->
        <div v-if="!isEditMode" class="space-y-6">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div class="card bg-base-100 shadow">
              <div class="card-body">
                <div class="flex items-center justify-between">
                  <h2 class="card-title text-lg gap-2">
                    <BanknotesIcon class="w-5 h-5" />
                    1) Devis
                  </h2>
                  <span class="badge badge-ghost">Obligatoire</span>
                </div>
                <div class="flex gap-2 items-end">
                  <TextInput
                    v-model="searchQuery"
                    label="Nom du devis"
                    placeholder="Ex: SO123"
                    class="flex-1"
                    @input="onSearchQueryInput"
                  />
                  <ButtonDaisy text="Rechercher" variant="primary" :icon="MagnifyingGlassIcon" :loader="isSearching" @click="onSearch" />
                  <ButtonDaisy
                    v-if="!isEditMode && selectedQuote && !isChildOrder"
                    text="Ajouter enfant"
                    variant="outline"
                    :icon="DocumentDuplicateIcon"
                    @click="showChildModal = true"
                  />
                </div>
                <p v-if="hasSearched && searchQuery && !isSearching && !selectedQuote" class="mt-2 text-sm text-base-content/50 italic">
                  Aucun devis trouvé pour "{{ searchQuery }}".
                </p>
                <div v-if="selectedQuote" class="mt-4 space-y-4">

                  <div class="p-4 bg-success/10 rounded-lg space-y-3">
                  <div class="flex items-center gap-2">
                    <p class="text-sm font-bold">{{ selectedQuote.quoteReference }}</p>
                    <a
                      v-if="(selectedQuote as any).odooUrl || selectedQuote.metadata?.odooUrl"
                      :href="(selectedQuote as any).odooUrl || selectedQuote.metadata.odooUrl"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="text-xs text-primary underline opacity-70 hover:opacity-100 flex items-center gap-0.5"
                      title="Voir dans Odoo"
                    >
                      <ArrowTopRightOnSquareIcon class="w-3.5 h-3.5" />
                      Odoo
                    </a>
                    <button
                      type="button"
                      class="btn btn-ghost btn-xs btn-circle"
                      :disabled="isReloadingQuote"
                      title="Resynchroniser le devis depuis Odoo"
                      aria-label="Resynchroniser le devis depuis Odoo"
                      @click="reloadSelectedQuote"
                    >
                      <ArrowPathIcon class="w-4 h-4" :class="{ 'animate-spin': isReloadingQuote }" />
                    </button>
                  </div>
                  <p
                    v-if="quoteUpdatedAgo"
                    class="text-xs opacity-60 flex items-center gap-1"
                    :title="quoteSyncedAgo ? `Synchronisé localement ${quoteSyncedAgo}` : undefined"
                  >
                    <ClockIcon class="w-3.5 h-3.5 shrink-0" />
                    Modifié dans Odoo {{ quoteUpdatedAgo }}
                  </p>
                  <p class="text-xs opacity-70 truncate">{{ selectedQuote.company?.name }}</p>
                  <div v-if="quoteDevisInfos" class="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 mt-3">
                    <div class="flex items-center gap-2 min-w-0">
                      <BanknotesIcon class="w-4 h-4 shrink-0 opacity-60" />
                      <div class="min-w-0">
                        <p class="text-xs opacity-60">
                          Total CA HT
                          <span v-if="quoteDevisInfos.totalCAHint" class="block font-normal opacity-70">{{
                            quoteDevisInfos.totalCAHint
                          }}</span>
                        </p>
                        <p class="font-medium text-sm">{{ quoteDevisInfos.totalCaUntaxed.toFixed(2) }} €</p>
                      </div>
                    </div>
                    <div class="flex items-center gap-2 min-w-0">
                      <ChartBarIcon class="w-4 h-4 shrink-0 opacity-60" />
                      <div class="min-w-0">
                        <p class="text-xs opacity-60">Marge Total</p>
                        <p class="font-medium text-sm">{{ quoteDevisInfos.margeTotal.toFixed(2) }} €</p>
                      </div>
                    </div>
                    <div class="flex items-center gap-2 min-w-0">
                      <ChartBarIcon class="w-4 h-4 shrink-0 opacity-60" />
                      <div class="min-w-0">
                        <p class="text-xs opacity-60 flex items-center gap-1">
                          Marge compteur
                          <span class="tooltip" :data-tip="`${detailCalculate}`">
                            <InformationCircleIcon class="w-4 h-4 shrink-0 opacity-60" />
                          </span>
                          <span v-if="isMargeForced" class="text-warning font-semibold">(forcée)</span>
                        </p>
                        <p v-if="margeCompteurAtValidationInfo" class="text-xs opacity-50 mt-0.5">{{ margeCompteurAtValidationInfo }}</p>
                        <template v-if="showMargeInput">
                          <div class="flex items-center gap-1 mt-0.5">
                            <input
                              v-model="forceMarge"
                              type="number"
                              step="0.01"
                              min="0"
                              class="input input-xs input-bordered w-24 font-medium text-sm"
                              :disabled="isFormReadOnly"
                              :readonly="!canForceMarge"
                              :class="{ 'cursor-default opacity-70': !canForceMarge }"
                              @input="isManualMarge = true"
                            />
                            <span class="text-sm">€</span>
                          </div>
                          <p v-if="canForceMarge && isMargeForced" class="text-xs opacity-50 mt-0.5">réel : {{ quoteDevisInfos.margeCompteur.toFixed(2) }} €</p>
                        </template>
                        <p v-else class="font-medium text-sm">
                          {{ effectiveMargeCompteur?.toFixed(2) ?? quoteDevisInfos.margeCompteur.toFixed(2) }} €
                        </p>
                      </div>
                    </div>
                    <div
                      class="flex items-center gap-2 min-w-0 rounded-lg transition-all"
                      :class="{ 'ring-2 ring-error ring-offset-2 ring-offset-base-100': showMissingFieldBorders && !sendAdvRequirements.signed }"
                    >
                      <CalendarDaysIcon class="w-4 h-4 shrink-0 opacity-60" />
                      <div class="min-w-0 flex items-center gap-2">
                        <div>
                          <p class="text-xs opacity-60">Signé le</p>
                          <p class="font-medium text-sm">
                            {{ quoteDevisInfos.signedAt }}
                            <span v-if="hasQuoteSignatureManuelle" class="text-xs text-warning ml-1">(manuel)</span>
                            <a
                              v-if="quoteDevisInfos.signedPdfUrl"
                              :href="quoteDevisInfos.signedPdfUrl"
                              target="_blank"
                              rel="noopener noreferrer"
                              class="link link-primary ml-1 inline-flex items-center gap-0.5"
                              title="Voir le devis signé"
                            >
                              <ArrowTopRightOnSquareIcon class="w-3.5 h-3.5" />
                            </a>
                          </p>
                        </div>
                        <button
                          v-if="canSignManually"
                          type="button"
                          class="btn btn-ghost btn-xs p-1 opacity-60 hover:opacity-100"
                          title="Signer manuellement le devis"
                          @click="showModalSignManually = true"
                        >
                          <PencilSquareIcon class="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <div class="flex items-center gap-2 min-w-0">
                      <ClockIcon class="w-4 h-4 shrink-0 opacity-60" />
                      <div class="min-w-0">
                        <p class="text-xs opacity-60">Durée</p>
                        <p class="font-medium text-sm">{{ quoteDevisInfos.duration }}</p>
                      </div>
                    </div>
                    <div
                      class="flex items-center gap-2 min-w-0 rounded-lg transition-all"
                      :class="{ 'ring-2 ring-error ring-offset-2 ring-offset-base-100': showMissingFieldBorders && !sendAdvRequirements.modePaiement }"
                    >
                      <CreditCardIcon class="w-4 h-4 shrink-0 opacity-60" />
                      <div class="min-w-0">
                        <p class="text-xs opacity-60">Mode paiement</p>
                        <p class="font-medium text-sm">{{ quoteDevisInfos.modePaiement }}</p>
                        <p v-if="!quoteDevisInfos.modePaiement || quoteDevisInfos.modePaiement === '-'" class="text-xs text-warning mt-1">
                          Modifiez-le sur Odoo puis re-synchronisez le devis (petit bouton de rechargement à droite du SOXXXXX).
                        </p>
                      </div>
                    </div>
                    <div class="min-w-0 sm:col-span-2">
                      <div class="form-control w-full">
                        <label class="label py-0"><span class="label-text font-semibold">Type contrat</span></label>
                        <select
                          v-model="typeContratCodeModel"
                          class="select select-bordered select-sm w-full transition-all"
                          :class="{ 'border-2 border-error': showMissingFieldBorders && !sendAdvRequirements.typeContrat }"
                        >
                          <option value="">—</option>
                          <option v-for="opt in contractTypeOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                        </select>
                      </div>
                    </div>
                    <div class="min-w-0 sm:col-span-2">
                      <div class="form-control w-full">
                        <label class="label py-0"><span class="label-text font-semibold">Famille</span></label>
                        <select
                          v-model="selectedFamilyCode"
                          class="select select-bordered select-sm w-full transition-all"
                          :class="{ 'border-2 border-error': showMissingFieldBorders && !sendAdvRequirements.famille }"
                        >
                          <option value="">—</option>
                          <option v-for="f in ventilationFamilies" :key="f.code" :value="f.code">{{ f.label }}</option>
                        </select>
                      </div>
                    </div>

                  </div>
                </div>
                 <div
                    v-if="hasQuoteSignatureManuelle"
                    class="p-4 rounded-lg border-2 transition-all"
                    :class="[
                      showMissingFieldBorders && !sendAdvRequirements.devisSigne
                        ? 'border-error ring-2 ring-error/50 bg-error/5'
                        : 'border-warning/50 bg-warning/10',
                    ]"
                  >
                    <p class="font-semibold text-warning mb-2">Devis signé manuellement</p>
                    <p class="text-sm text-base-content/80 mb-3">
                      L'upload du devis signé (PDF) est obligatoire pour envoyer ADV.
                    </p>
                    <OrderFileInput
                      :existing-files="orderFilesSignedQuote.map((f: any) => ({ uuid: f.uuid, fileName: f.fileName ?? f.file_name ?? '', path: f.path ?? '' }))"
                      :required="true"
                      accept=".pdf,.PDF"
                      label="Devis signé (PDF)"
                      add-button-label="Uploader le devis signé"
                      :disabled="isFinalized || isProjectRead"
                      @change="onSignedQuoteFileChange"
                      @delete-file="onDeleteFile"
                    />
                  </div>
              </div>
            </div>
          </div>
            <div class="card bg-base-100 shadow h-full">
              <div class="card-body">
                <h2 class="card-title text-lg">
                  <BuildingOffice2Icon class="w-5 h-5" />
                  Client
                </h2>
                <div v-if="selectedQuote?.company" class="p-4 bg-base-200/50 rounded-lg mt-2 space-y-4">
                  <div class="flex gap-3 min-w-0">
                    <BuildingOffice2Icon class="w-4 h-4 shrink-0 opacity-60 mt-0.5" />
                    <div class="min-w-0">
                      <p class="text-xs opacity-70">Raison sociale</p>
                      <p class="font-semibold truncate">{{ selectedQuote.company?.name }}</p>
                    </div>
                  </div>
                  <div class="flex gap-3 flex-wrap">
                    <div class="flex items-start gap-2 min-w-0">
                      <DocumentTextIcon class="w-4 h-4 shrink-0 opacity-60 mt-0.5" />
                      <div>
                        <p class="text-xs opacity-70">SIREN</p>
                        <p class="font-medium text-sm">{{ selectedQuote.company?.siren || '-' }}</p>
                      </div>
                    </div>
                    <div class="flex items-start gap-2 min-w-0">
                      <DocumentTextIcon class="w-4 h-4 shrink-0 opacity-60 mt-0.5" />
                      <div>
                        <p class="text-xs opacity-70">SIRET</p>
                        <p class="font-medium text-sm">{{ selectedQuote.company?.siret || '-' }}</p>
                      </div>
                    </div>
                  </div>
                  <div class="flex gap-3 min-w-0">
                    <UserCircleIcon class="w-4 h-4 shrink-0 opacity-60 mt-0.5" />
                    <div class="min-w-0">
                      <p class="text-xs opacity-70">Commercial</p>
                      <p class="text-sm font-medium">{{ commercialDisplay }}</p>
                      <p class="text-xs opacity-60 flex items-center gap-1 mt-0.5">
                        <EnvelopeIcon class="w-3.5 h-3.5 shrink-0" />
                        {{ selectedQuote.salesEngineer?.email || '-' }}
                      </p>
                    </div>
                  </div>
                  <div v-if="iavDisplay" class="flex gap-3 min-w-0">
                    <UserCircleIcon class="w-4 h-4 shrink-0 opacity-60 mt-0.5" />
                    <div class="min-w-0">
                      <p class="text-xs opacity-70">IAV</p>
                      <p class="text-sm font-medium">{{ iavDisplay }}</p>
                      <p class="text-xs opacity-60 flex items-center gap-1 mt-0.5">
                        <EnvelopeIcon class="w-3.5 h-3.5 shrink-0" />
                        {{ selectedQuote.iav?.email || '-' }}
                      </p>
                    </div>
                  </div>
                  <div v-if="advAssigneDisplay || isAdv" class="flex gap-3 min-w-0">
                    <UserCircleIcon class="w-4 h-4 shrink-0 opacity-60 mt-0.5" />
                    <div class="min-w-0 w-full">
                      <p class="text-xs opacity-70">ADV assigné</p>
                      <template v-if="isAdv">
                        <select
                          v-model="selectedAdvUuid"
                          class="select select-bordered select-xs w-full mt-1"
                          :disabled="isLoadingAdvUsers || isSwitchingAdv"
                          @change="onAdvChange(selectedAdvUuid!)"
                        >
                          <option :value="null">— Aucun —</option>
                          <option v-for="user in advUsers" :key="user.uuid" :value="user.uuid">
                            {{ user.firstName }} {{ user.lastName }}
                          </option>
                        </select>
                      </template>
                      <template v-else>
                        <p class="text-sm font-medium">{{ advAssigneDisplay }}</p>
                        <p class="text-xs opacity-60 flex items-center gap-1 mt-0.5">
                          <EnvelopeIcon class="w-3.5 h-3.5 shrink-0" />
                          {{ orderStore.currentOrder?.adv?.email || orderStore.currentOrder?.assignedTo?.email || '-' }}
                        </p>
                      </template>
                    </div>
                  </div>
                  <div class="flex gap-3 min-w-0">
                    <MapPinIcon class="w-4 h-4 shrink-0 opacity-60 mt-0.5" />
                    <div class="min-w-0">
                      <p class="text-xs opacity-70">Site de facturation</p>
                      <p v-if="siteFacturationDisplay.name" class="text-sm font-medium">{{ siteFacturationDisplay.name }}</p>
                      <p class="text-sm opacity-80">{{ siteFacturationDisplay.address || 'Information non saisie' }}</p>
                    </div>
                  </div>
                  <div class="flex gap-3 min-w-0">
                    <MapPinIcon class="w-4 h-4 shrink-0 opacity-60 mt-0.5" />
                    <div class="min-w-0">
                      <p class="text-xs opacity-70">Site de livraison</p>
                      <p v-if="siteLivraisonDisplay.name" class="text-sm font-medium">{{ siteLivraisonDisplay.name }}</p>
                      <p class="text-sm opacity-80">{{ siteLivraisonDisplay.address || 'Information non saisie' }}</p>
                    </div>
                  </div>
                  <div v-if="hasChild" class="mt-3 border-t border-base-300 pt-2">
                    <button
                      class="flex items-center justify-between w-full rounded-lg px-3 py-2.5 hover:bg-base-200 transition-colors cursor-pointer"
                      @click="showChildModal = true"
                    >
                      <span class="flex items-center gap-2 text-sm font-medium">
                        <DocumentDuplicateIcon class="w-4 h-4 opacity-60" />
                        Commandes liées
                        <span class="badge badge-neutral badge-xs">{{ selectedChildren.length }}</span>
                        <span v-if="childrenHavePendingChanges" class="badge badge-warning badge-xs gap-1" title="Modifications non enregistrées">
                          <span class="w-1.5 h-1.5 rounded-full bg-warning-content/80" />
                          Modifs en attente
                        </span>
                      </span>
                      <span class="text-xs text-primary font-medium">Ajouter / Gérer →</span>
                    </button>
                    <div class="mt-1 space-y-1 px-3">
                      <div
                        v-for="child in selectedChildren"
                        :key="child.orderUuid || child.quoteReference"
                        class="flex items-center justify-between py-1"
                      >
                        <a
                          v-if="child.orderUuid"
                          :href="`/dashboard/order/${child.orderUuid}`"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="text-sm font-medium text-primary hover:underline inline-flex items-center gap-1"
                          @click.stop
                        >
                          {{ child.quoteReference }}
                          <ArrowTopRightOnSquareIcon class="w-3.5 h-3.5 opacity-60 shrink-0" />
                        </a>
                        <span v-else class="text-sm font-medium">{{ child.quoteReference }}</span>
                        <span class="badge badge-xs" :class="child.followParentWorkflow ? 'badge-info' : 'badge-ghost'">
                          {{ child.followParentWorkflow ? 'Suit principale' : 'Indépendante' }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <p v-else class="text-sm text-base-content/50 italic mt-2">Sélectionne un devis pour afficher le client.</p>
              </div>
            </div>
          </div>
          <!-- Ligne 2: Type de formulaire + Lead/apporteur -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div class="card bg-base-100 shadow h-full">
              <div class="card-body">
                <div class="flex items-center justify-between">
                  <h2 class="card-title text-lg gap-2">
                    <DocumentDuplicateIcon class="w-5 h-5" />
                    2) Formulaire
                  </h2>
                </div>
                <div v-if="selectedQuote" class="space-y-3">
                  <p class="text-sm font-medium text-base-content/70">Choisis le type de formulaire <span class="text-primary">*</span></p>
                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <button
                      v-for="opt in formTypeOptions"
                      :key="opt.id"
                      type="button"
                      class="group relative flex items-center gap-4 p-4 rounded-xl border-2 transition-all duration-200 text-left"
                      :class="
                        selectedFormType === opt.value
                          ? 'border-primary bg-primary/10 shadow-sm'
                          : 'border-base-300 hover:border-primary/50 hover:bg-base-200/80'
                      "
                      @click="selectedFormType = opt.value"
                    >
                      <div
                        class="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg transition-colors"
                        :class="
                          selectedFormType === opt.value ? 'bg-primary/20 text-primary' : 'bg-base-200 text-base-content/60 group-hover:bg-base-300'
                        "
                      >
                        <component :is="opt.logoIcon || DocumentDuplicateIcon" class="h-5 w-5" />
                      </div>
                      <span class="flex-1 font-medium" :class="selectedFormType === opt.value ? 'text-primary' : 'text-base-content'">
                        {{ opt.label }}
                      </span>
                      <CheckCircleIcon v-if="selectedFormType === opt.value" class="h-6 w-6 shrink-0 text-primary" />
                    </button>
                  </div>
                </div>
                <div v-else class="flex flex-col items-center justify-center h-full text-center py-4">
                  <p class="text-sm text-base-content/50 italic">Sélectionne d'abord un devis à l'étape 1.</p>
                </div>
              </div>
            </div>
            <div class="card bg-base-100 shadow h-full">
              <div class="card-body flex flex-col">
                <h2 class="card-title text-lg gap-2">
                  <UserPlusIcon class="w-5 h-5" />
                  Lead / Apporteur d'affaires
                </h2>
                <p class="text-xs opacity-70 flex items-center gap-1 mt-0.5 max-h-fit">
                  <InformationCircleIcon class="w-3.5 h-3.5 shrink-0" />
                  Un seul des deux peut être associé au devis.
                </p>
                <div v-if="selectedQuote?.company" class="mt-3 p-4 bg-base-200/50 rounded-lg flex flex-col items-start">
                  <div v-if="hasLead" class="flex flex-col items-start gap-2 p-3 bg-success/10 rounded-lg w-full">
                    <div class="flex items-center gap-2">
                      <UserPlusIcon class="w-4 h-4 shrink-0 opacity-60" />
                      <span class="text-xs opacity-70">{{ isLeadLocked ? 'Rattaché automatiquement' : 'Lead associé' }}</span>
                      <router-link
                        v-if="displayedLead?.uuid"
                        :to="`/dashboard/leads/${displayedLead.uuid}`"
                        target="_blank"
                        class="link link-primary inline-flex items-center"
                        title="Ouvrir la fiche du lead"
                      >
                        <ArrowTopRightOnSquareIcon class="w-3.5 h-3.5" />
                      </router-link>
                    </div>
                    <div class="flex flex-wrap items-center gap-2">
                      <span class="badge badge-sm badge-neutral">{{ displayedLead?.type }}</span>
                      <span v-if="displayedLead?.status" class="badge badge-sm badge-success">{{ leadStatusLabel(displayedLead?.status) }}</span>
                    </div>
                    <div class="flex gap-2 w-full">
                      <div v-if="displayedLead?.companyName || displayedLead?.companySiret" class="text-sm">
                        <div class="text-xs opacity-60">Société lead</div>
                        <p class="font-medium">{{ displayedLead?.companyName }}</p>
                        <p v-if="displayedLead?.companySiret" class="text-xs opacity-60">SIRET: {{ displayedLead?.companySiret }}</p>
                      </div>
                      <div v-if="displayedLead?.contactFirstname || displayedLead?.contactLastname || displayedLead?.contactEmail" class="text-sm">
                        <div class="text-xs opacity-60">Contact lead</div>
                        <p class="font-medium">{{ displayedLead?.contactFirstname }} {{ displayedLead?.contactLastname }}</p>
                        <p v-if="displayedLead?.contactEmail" class="text-xs opacity-60">{{ displayedLead?.contactEmail }}</p>
                      </div>
                    </div>
                    <div class="text-sm">
                      <div class="text-xs opacity-60">Auteur</div>
                      <p class="font-medium">{{ displayedLead?.author?.firstName }} {{ displayedLead?.author?.lastName }}</p>
                      <p v-if="displayedLead?.author?.email" class="text-xs opacity-60">{{ displayedLead?.author?.email }}</p>
                    </div>
                    <p v-if="displayedLead?.createdAt" class="text-xs opacity-60">Reçu le {{ formatDate(displayedLead?.createdAt) }}</p>
                    <button v-if="!isLeadLocked && isSuperAdmin" class="btn btn-ghost btn-sm w-fit mt-1" :disabled="isFormReadOnly" @click="dissociateLead">Dissocier</button>
                  </div>
                  <div v-else-if="canSelectLead && companyLeads.length" class="flex flex-col gap-2">
                    <div class="flex items-center gap-2 max-h-fit">
                      <UserPlusIcon class="w-4 h-4 shrink-0 opacity-60" />
                      <span class="text-xs opacity-70">Associer un lead</span>
                    </div>
                    <select
                      :model-value="selectedLeadUuid"
                      class="select select-bordered select-sm w-full"
                      :disabled="isFormReadOnly"
                      @change="onLeadChange(($event.target as HTMLSelectElement).value || null)"
                    >
                      <option value="">— Aucun —</option>
                      <option v-for="l in companyLeads" :key="l.uuid" :value="l.uuid">
                        {{ l.companyName }} — {{ l.companySiret }} · {{ l.author?.firstName }} {{ l.author?.lastName }} ({{ l.type }})
                      </option>
                    </select>
                  </div>
                  <div
                    v-else-if="canSelectLead && !isLoadingLeads && !companyLeads.length"
                    class="flex flex-col items-center justify-center flex-1 py-6 text-center"
                  >
                    <p class="text-sm text-base-content/50 italic">Aucun lead déclaré sur cette entreprise.</p>
                  </div>
                </div>
                <p v-else class="text-sm text-base-content/50 italic mt-3 text-center">Sélectionne un devis pour gérer lead.</p>
              </div>
            </div>
          </div>
        </div>
        <div v-if="isEditMode && selectedQuote" class="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          <div class="card bg-base-100 shadow">
            <div class="card-body">
              <div class="flex items-center justify-between gap-2">
                <h2 class="card-title text-lg gap-2 flex-wrap items-center min-w-0">
                  <BanknotesIcon class="w-5 h-5 shrink-0" />
                  <span class="flex items-center gap-1 flex-wrap min-w-0">
                    <span>Devis {{ selectedQuote.quoteReference }}</span>
                    <div class="relative inline-flex shrink-0 group/odoo-sync">
                      <button
                        type="button"
                        class="btn btn-ghost btn-xs btn-circle"
                        :disabled="isResyncingQuote || isLoadingOrder"
                        aria-label="Synchroniser le devis depuis Odoo"
                      >
                        <ArrowPathIcon class="w-4 h-4" :class="{ 'animate-spin': isResyncingQuote }" />
                      </button>
                      <div
                        class="absolute left-1/2 z-[100] -translate-x-1/2 top-full pt-2 w-[min(22rem,calc(100vw-2rem))] min-w-[17rem]
                          opacity-0 invisible pointer-events-none
                          group-hover/odoo-sync:opacity-100 group-hover/odoo-sync:visible group-hover/odoo-sync:pointer-events-auto
                          transition-opacity duration-150"
                      >
                        <div class="card bg-base-100 shadow-xl border border-base-200 p-4">
                          <p class="text-sm text-base-content/80 mb-3">
                            Appel la synchro forcée Odoo, puis recharge la commande et le
                            formulaire.
                          </p>
                          <button
                            type="button"
                            class="btn btn-primary btn-sm w-full gap-2"
                            :disabled="isResyncingQuote"
                            @click="resyncQuoteFromOdoo"
                          >
                            <ArrowPathIcon class="w-4 h-4" />
                            Forcer sync Odoo
                          </button>
                        </div>
                      </div>
                    </div>
                  </span>
                  <a
                    v-if="(selectedQuote as any).odooUrl || selectedQuote.metadata?.odooUrl"
                    :href="(selectedQuote as any).odooUrl || selectedQuote.metadata.odooUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-xs font-normal text-primary underline opacity-70 hover:opacity-100 flex items-center gap-0.5 shrink-0"
                    title="Voir dans Odoo"
                  >
                    <ArrowTopRightOnSquareIcon class="w-3.5 h-3.5" />
                    Odoo
                  </a>
                </h2>
                <span
                  v-if="statusLabel"
                  class="badge"
                  :class="
                    isValidated
                      ? 'badge-success'
                      : isCanceled
                        ? 'badge-error'
                        : isWaiting
                          ? 'badge-warning'
                          : isSentToBuManager
                            ? 'bg-[#DA70D6] text-white'
                            : isDraftOrder
                              ? 'badge-ghost'
                              : 'badge-info'
                  "
                >
                  {{ statusLabel }}
                </span>
              </div>
              <span
                v-if="quoteUpdatedAgo"
                class="text-xs font-normal opacity-60 inline-flex items-center gap-1 mt-1"
                :title="quoteSyncedAgo ? `Synchronisé localement ${quoteSyncedAgo}` : undefined"
              >
                <ClockIcon class="w-3.5 h-3.5 shrink-0" />
                Modifié dans Odoo {{ quoteUpdatedAgo }}
              </span>
              <div v-if="quoteDevisInfos" class="p-4 bg-success/10 rounded-lg border-2 border-success/50 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 mt-3">
                <div class="flex items-center gap-2 min-w-0">
                  <BanknotesIcon class="w-4 h-4 shrink-0 opacity-60" />
                  <div class="min-w-0">
                    <p class="text-xs opacity-60">
                      Total CA
                      <span v-if="quoteDevisInfos.totalCAHint" class="block font-normal opacity-70">{{
                        quoteDevisInfos.totalCAHint
                      }}</span>
                    </p>
                    <p class="font-medium text-sm">{{ quoteDevisInfos.totalCaUntaxed.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}} €</p>
                  </div>
                </div>
                <div class="flex items-center gap-2 min-w-0">
                  <ChartBarIcon class="w-4 h-4 shrink-0 opacity-60" />
                  <div class="min-w-0">
                    <p class="text-xs opacity-60">Marge Total</p>
                    <p class="font-medium text-sm">{{ quoteDevisInfos.margeTotal.toFixed(2) }} €</p>
                  </div>
                </div>
                <div class="flex items-center gap-2 min-w-0">
                  <ChartBarIcon class="w-4 h-4 shrink-0 opacity-60" />
                  <div class="min-w-0">
                    <p class="text-xs opacity-60 flex items-center gap-1">
                      Marge compteur
                      <span class="tooltip" :data-tip="`${detailCalculate}`">
                        <InformationCircleIcon class="w-4 h-4 shrink-0 opacity-60" />
                      </span>
                      <span v-if="isMargeForced" class="text-warning font-semibold">(forcée)</span>
                    </p>
                    <p v-if="margeCompteurAtValidationInfo" class="text-xs opacity-50 mt-0.5">{{ margeCompteurAtValidationInfo }}</p>
                    <template v-if="showMargeInput">
                      <div class="flex items-center gap-1 mt-0.5">
                        <input
                          v-model="forceMarge"
                          type="number"
                          step="0.01"
                          min="0"
                          class="input input-xs input-bordered w-24 font-medium text-sm"
                          :disabled="isFormReadOnly"
                          :readonly="!canForceMarge"
                          :class="{ 'cursor-default opacity-70': !canForceMarge }"
                          @input="isManualMarge = true"
                        />
                        <span class="text-sm">€</span>
                      </div>
                      <p v-if="canForceMarge && isMargeForced" class="text-xs opacity-50 mt-0.5">réel : {{ quoteDevisInfos.margeCompteur.toFixed(2) }} €</p>
                    </template>
                    <p v-else class="font-medium text-sm">{{ effectiveMargeCompteur?.toFixed(2) ?? quoteDevisInfos.margeCompteur.toFixed(2) }} €</p>
                  </div>
                </div>
                <div
                  class="flex items-center gap-2 min-w-0 rounded-lg transition-all"
                  :class="{ 'ring-2 ring-error ring-offset-2 ring-offset-base-100': showMissingFieldBorders && !sendAdvRequirements.signed }"
                >
                  <CalendarDaysIcon class="w-4 h-4 shrink-0 opacity-60" />
                  <div class="min-w-0 flex items-center gap-2">
                    <div>
                      <p class="text-xs opacity-60">Signé le</p>
                      <p class="font-medium text-sm">
                        {{ quoteDevisInfos.signedAt }}
                        <span v-if="hasQuoteSignatureManuelle" class="text-xs text-warning ml-1">(manuel)</span>
                        <a
                          v-if="quoteDevisInfos.signedPdfUrl"
                          :href="quoteDevisInfos.signedPdfUrl"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="link link-primary ml-1 inline-flex items-center gap-0.5"
                          title="Voir le devis signé"
                        >
                          <ArrowTopRightOnSquareIcon class="w-3.5 h-3.5" />
                        </a>
                      </p>
                    </div>
                    <button
                      v-if="canSignManually"
                      type="button"
                      class="btn btn-ghost btn-xs p-1 opacity-60 hover:opacity-100"
                      title="Signer manuellement le devis"
                      @click="showModalSignManually = true"
                    >
                      <PencilSquareIcon class="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div class="flex items-center gap-2 min-w-0">
                  <ClockIcon class="w-4 h-4 shrink-0 opacity-60" />
                  <div class="min-w-0">
                    <p class="text-xs opacity-60">Durée</p>
                    <p class="font-medium text-sm">{{ quoteDevisInfos.duration }}</p>
                  </div>
                </div>
                <div
                  class="flex items-center gap-2 min-w-0 rounded-lg transition-all"
                  :class="{ 'ring-2 ring-error ring-offset-2 ring-offset-base-100': showMissingFieldBorders && !sendAdvRequirements.modePaiement }"
                >
                  <CreditCardIcon class="w-4 h-4 shrink-0 opacity-60" />
                  <div class="min-w-0">
                    <p class="text-xs opacity-60">Mode paiement</p>
                    <p class="font-medium text-sm">{{ quoteDevisInfos.modePaiement }}</p>
                    <p v-if="!quoteDevisInfos.modePaiement || quoteDevisInfos.modePaiement === '-'" class="text-xs text-warning mt-1">
                      Modifiez-le sur Odoo puis réactualisez la page.
                    </p>
                  </div>
                </div>
                <div class="flex items-center gap-2 min-w-0">
                  <DocumentTextIcon class="w-4 h-4 shrink-0 opacity-60" />
                  <div class="min-w-0">
                    <p class="text-xs opacity-60">Type commande</p>
                    <p class="font-medium text-sm">{{ quoteDevisInfos.orderType }}</p>
                  </div>
                </div>
                <div class="min-w-0 sm:col-span-2">
                  <div class="form-control w-full">
                    <label class="label py-0"><span class="label-text font-semibold">Type de contrat</span></label>
                    <select
                      v-model="typeContratCodeModel"
                      class="select select-bordered select-sm w-full transition-all"
                      :class="{ 'border-2 border-error': showMissingFieldBorders && !sendAdvRequirements.typeContrat }"
                      :disabled="isFormReadOnly"
                    >
                      <option value="">—</option>
                      <option v-for="opt in contractTypeOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                    </select>
                  </div>
                </div>
                <div class="min-w-0 sm:col-span-2">
                  <div class="form-control w-full">
                    <label class="label py-0"><span class="label-text font-semibold">Famille</span></label>
                    <select
                      v-model="selectedFamilyCode"
                      class="select select-bordered select-sm w-full transition-all"
                      :class="{ 'border-2 border-error': showMissingFieldBorders && !sendAdvRequirements.famille }"
                      :disabled="isFormReadOnly"
                    >
                      <option value="">—</option>
                      <option v-for="f in ventilationFamilies" :key="f.code" :value="f.code">{{ f.label }}</option>
                    </select>
                  </div>
                </div>

              </div>
              <div
                v-if="salesManagerValidationInfo"
                class="mt-3 p-4 rounded-lg border-2 border-[#DA70D6]/50 bg-[#DA70D6]/10"
              >
                <p class="font-semibold mb-2" style="color: #DA70D6">Validé par le manager</p>
                <div class="flex flex-wrap items-center gap-4 text-sm">
                  <div class="flex items-center gap-2">
                    <UserCircleIcon class="w-4 h-4 shrink-0 opacity-60" />
                    <span>{{ salesManagerValidationInfo.name }}</span>
                  </div>
                  <div class="flex items-center gap-2">
                    <CalendarDaysIcon class="w-4 h-4 shrink-0 opacity-60" />
                    <span>{{ salesManagerValidationInfo.date }}</span>
                  </div>
                  <div class="flex items-center gap-2">
                    <ClockIcon class="w-4 h-4 shrink-0 opacity-60" />
                    <span>{{ salesManagerValidationInfo.time }}</span>
                  </div>
                </div>
              </div>
              <div
                    v-if="hasQuoteSignatureManuelle"
                    class="mt-3 p-4 rounded-lg border-2 transition-all"
                    :class="[
                      showMissingFieldBorders && !sendAdvRequirements.devisSigne
                        ? 'border-error ring-2 ring-error/50 bg-error/5'
                        : 'border-warning/50 bg-warning/10',
                    ]"
                  >
                    <p class="font-semibold text-warning mb-2">Devis signé manuellement</p>
                    <p class="text-sm text-base-content/80 mb-3">
                      L'upload du devis signé (PDF) est obligatoire pour envoyer ADV.
                    </p>
                    <OrderFileInput
                      :existing-files="orderFilesSignedQuote.map((f: any) => ({ uuid: f.uuid, fileName: f.fileName ?? f.file_name ?? '', path: f.path ?? '' }))"
                      :required="true"
                      accept=".pdf,.PDF"
                      label="Devis signé (PDF)"
                      add-button-label="Uploader le devis signé"
                      :disabled="isFinalized || isProjectRead"
                      @change="onSignedQuoteFileChange"
                      @delete-file="onDeleteFile"
                    />
                  </div>
            </div>
          </div>
          <div class="card bg-base-100 shadow">
            <div class="card-body">
              <h2 class="card-title text-lg">
                <BuildingOffice2Icon class="w-5 h-5" />
                Client
              </h2>
              <div v-if="selectedQuote?.company" class="p-4 bg-base-200/50 rounded-lg border-2 border-base-300 mt-2 space-y-4">
                <div class="flex gap-3 min-w-0">
                  <BuildingOffice2Icon class="w-4 h-4 shrink-0 opacity-60 mt-0.5" />
                  <div class="min-w-0">
                    <p class="text-xs opacity-70">Raison sociale</p>
                    <p class="font-semibold truncate">{{ selectedQuote.company?.name }}</p>
                  </div>
                </div>
                <div class="flex gap-3 flex-wrap">
                  <div class="flex items-start gap-2 min-w-0">
                    <DocumentTextIcon class="w-4 h-4 shrink-0 opacity-60 mt-0.5" />
                    <div>
                      <p class="text-xs opacity-70">SIREN</p>
                      <p class="font-medium text-sm">{{ selectedQuote.company?.siren || '-' }}</p>
                    </div>
                  </div>
                  <div class="flex items-start gap-2 min-w-0">
                    <DocumentTextIcon class="w-4 h-4 shrink-0 opacity-60 mt-0.5" />
                    <div>
                      <p class="text-xs opacity-70">SIRET</p>
                      <p class="font-medium text-sm">{{ selectedQuote.company?.siret || '-' }}</p>
                    </div>
                  </div>
                </div>
                <div class="flex gap-3 min-w-0">
                  <UserCircleIcon class="w-4 h-4 shrink-0 opacity-60 mt-0.5" />
                  <div class="min-w-0">
                    <p class="text-xs opacity-70">Commercial</p>
                    <p class="text-sm font-medium">{{ commercialDisplay }}</p>
                    <p class="text-xs opacity-60 flex items-center gap-1 mt-0.5">
                      <EnvelopeIcon class="w-3.5 h-3.5 shrink-0" />
                      {{ selectedQuote.salesEngineer?.email || '-' }}
                    </p>
                  </div>
                </div>
                <div v-if="iavDisplay" class="flex gap-3 min-w-0">
                  <UserCircleIcon class="w-4 h-4 shrink-0 opacity-60 mt-0.5" />
                  <div class="min-w-0">
                    <p class="text-xs opacity-70">IAV</p>
                    <p class="text-sm font-medium">{{ iavDisplay }}</p>
                    <p class="text-xs opacity-60 flex items-center gap-1 mt-0.5">
                      <EnvelopeIcon class="w-3.5 h-3.5 shrink-0" />
                      {{ selectedQuote.iav?.email || '-' }}
                    </p>
                  </div>
                </div>
                <div v-if="advAssigneDisplay || isAdv" class="flex gap-3 min-w-0">
                  <UserCircleIcon class="w-4 h-4 shrink-0 opacity-60 mt-0.5" />
                  <div class="min-w-0 w-full">
                    <p class="text-xs opacity-70">ADV assigné</p>
                    <template v-if="isAdv">
                      <select
                        v-model="selectedAdvUuid"
                        class="select select-bordered select-xs w-full mt-1"
                        :disabled="isLoadingAdvUsers || isSwitchingAdv"
                        @change="onAdvChange(selectedAdvUuid!)"
                      >
                        <option v-for="user in advUsers" :key="user.uuid" :value="user.uuid">
                          {{ user.firstName }} {{ user.lastName }}
                        </option>
                      </select>
                    </template>
                    <template v-else>
                      <p class="text-sm font-medium">{{ advAssigneDisplay }}</p>
                      <p class="text-xs opacity-60 flex items-center gap-1 mt-0.5">
                        <EnvelopeIcon class="w-3.5 h-3.5 shrink-0" />
                        {{ orderStore.currentOrder?.adv?.email || orderStore.currentOrder?.assignedTo?.email || '-' }}
                      </p>
                    </template>
                  </div>
                </div>
                <div class="flex gap-3 min-w-0">
                  <MapPinIcon class="w-4 h-4 shrink-0 opacity-60 mt-0.5" />
                  <div class="min-w-0">
                    <p class="text-xs opacity-70">Site de facturation</p>
                    <p v-if="siteFacturationDisplay.name" class="text-sm font-medium">{{ siteFacturationDisplay.name }}</p>
                    <p class="text-sm opacity-80">{{ siteFacturationDisplay.address || 'Information non saisie' }}</p>
                  </div>
                </div>
                <div class="flex gap-3 min-w-0">
                  <MapPinIcon class="w-4 h-4 shrink-0 opacity-60 mt-0.5" />
                  <div class="min-w-0">
                    <p class="text-xs opacity-70">Site de livraison</p>
                    <p v-if="siteLivraisonDisplay.name" class="text-sm font-medium">{{ siteLivraisonDisplay.name }}</p>
                    <p class="text-sm opacity-80">{{ siteLivraisonDisplay.address || 'Information non saisie' }}</p>
                  </div>
                </div>

                <!-- Lien vers la commande principale -->
                <div v-if="isChildOrder && parentalOrderData" class="pt-2 border-t border-base-300">
                  <a
                    :href="`/dashboard/order/${parentalOrderData.uuid}`"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="flex items-center justify-between w-full rounded-lg px-3 py-2.5 hover:bg-base-200 transition-colors"
                  >
                    <span class="flex items-center gap-2 text-sm font-medium">
                      <LinkIcon class="w-4 h-4 opacity-60" />
                      Commande principale
                    </span>
                    <span class="flex items-center gap-1 text-xs text-primary font-medium">
                      {{ parentalOrderData.quoteReference }}
                      <ArrowTopRightOnSquareIcon class="w-3.5 h-3.5" />
                    </span>
                  </a>
                </div>

                <div
                  v-if="!isChildOrder"
                  class="pt-2 border-t transition-colors"
                  :class="showMissingFieldBorders && sendAdvRequirements.enfantsSignes === false ? 'border-error' : 'border-base-300'"
                >
                  <button
                    class="flex items-center justify-between w-full rounded-lg px-3 py-2.5 transition-colors"
                    :class="canManageChildren ? 'hover:bg-base-200 cursor-pointer' : 'cursor-default opacity-60'"
                    @click="canManageChildren && (showChildModal = true)"
                  >
                    <span class="flex items-center gap-2 text-sm font-medium">
                      <DocumentDuplicateIcon class="w-4 h-4 opacity-60" />
                      Commandes liées
                      <span v-if="hasChild" class="badge badge-neutral badge-xs">{{ selectedChildren.length }}</span>
                      <span v-if="childrenHavePendingChanges" class="badge badge-warning badge-xs gap-1" title="Modifications non enregistrées">
                        <span class="w-1.5 h-1.5 rounded-full bg-warning-content/80" />
                        Modifs en attente
                      </span>
                      <span v-if="showMissingFieldBorders && sendAdvRequirements.enfantsSignes === false" class="badge badge-error badge-xs">!</span>
                    </span>
                    <span v-if="canManageChildren" class="text-xs text-primary font-medium">Ajouter / Gérer →</span>
                    <span v-else class="text-xs opacity-40">Lecture seule</span>
                  </button>
                  <div v-if="hasChild" class="mt-1 space-y-1 px-3">
                    <div
                      v-for="child in selectedChildren"
                      :key="child.orderUuid || child.quoteReference"
                      class="flex items-center justify-between py-1 text-xs"
                    >
                      <a
                        v-if="child.orderUuid"
                        :href="`/dashboard/order/${child.orderUuid}`"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="font-medium text-sm text-primary hover:underline inline-flex items-center gap-1"
                        @click.stop
                      >
                        {{ child.quoteReference }}
                        <ArrowTopRightOnSquareIcon class="w-3.5 h-3.5 opacity-60 shrink-0" />
                      </a>
                      <span v-else class="font-medium text-sm">{{ child.quoteReference }}</span>
                      <div class="flex items-center gap-1">
                        <span
                          v-if="child.followParentWorkflow && child.exists && !child.signedAt && !child.signedManuallyAt"
                          class="badge badge-error badge-xs"
                        >Non signé</span>
                        <span
                          class="badge badge-xs"
                          :class="child.followParentWorkflow ? 'badge-info' : 'badge-ghost'"
                        >
                          {{ child.followParentWorkflow ? 'Suit principale' : 'Indépendante' }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div><!-- fin v-if="!isChildOrder" section enfants -->

              </div>
            </div>
          </div>
          <div v-if="hasLead || (!showOperatorCommentBlock && !operatorComments.length)" class="card bg-base-100 shadow h-full">
            <div class="card-body flex flex-col">
              <h2 class="card-title text-lg gap-2">
                <UserPlusIcon class="w-5 h-5" />
                Lead
              </h2>
              <p class="text-xs opacity-70 flex items-center gap-1 mt-0.5 max-h-fit">
                <InformationCircleIcon class="w-3.5 h-3.5 shrink-0" />
                Associer un lead à la commande.
              </p>
              <div v-if="selectedQuote?.company" class="mt-3 p-4 bg-base-200/50 rounded-lg flex flex-col items-start">
                <div v-if="hasLead" class="flex flex-col items-start gap-2 p-3 bg-success/10 rounded-lg w-full">
                  <div class="flex items-center gap-2">
                    <UserPlusIcon class="w-4 h-4 shrink-0 opacity-60" />
                    <span class="text-xs opacity-70">{{ isLeadLocked ? 'Rattaché automatiquement' : 'Lead associé' }}</span>
                    <router-link
                      v-if="displayedLead?.uuid"
                      :to="`/dashboard/leads/${displayedLead.uuid}`"
                      target="_blank"
                      class="link link-primary inline-flex items-center"
                      title="Ouvrir la fiche du lead"
                    >
                      <ArrowTopRightOnSquareIcon class="w-3.5 h-3.5" />
                    </router-link>
                  </div>
                  <div class="flex flex-wrap items-center gap-2">
                    <span class="badge badge-sm badge-neutral">{{ displayedLead?.type }}</span>
                    <span v-if="displayedLead?.status" class="badge badge-sm badge-success">{{ leadStatusLabel(displayedLead?.status) }}</span>
                  </div>
                  <div v-if="displayedLead?.companyName || displayedLead?.companySiret" class="text-sm">
                    <div class="text-xs opacity-60">Société lead</div>
                    <p class="font-medium">{{ displayedLead?.companyName }}</p>
                    <p v-if="displayedLead?.companySiret" class="text-xs opacity-60">SIRET: {{ displayedLead?.companySiret }}</p>
                  </div>
                  <div class="text-sm">
                    <div class="text-xs opacity-60">Auteur</div>
                    <p class="font-medium">{{ displayedLead?.author?.firstName }} {{ displayedLead?.author?.lastName }}</p>
                    <p v-if="displayedLead?.author?.email" class="text-xs opacity-60">{{ displayedLead?.author?.email }}</p>
                  </div>
                  <div v-if="displayedLead?.contactFirstname || displayedLead?.contactLastname || displayedLead?.contactEmail" class="text-sm">
                    <div class="text-xs opacity-60">Contact lead</div>
                    <p class="font-medium">{{ displayedLead?.contactFirstname }} {{ displayedLead?.contactLastname }}</p>
                    <p v-if="displayedLead?.contactEmail" class="text-xs opacity-60">{{ displayedLead?.contactEmail }}</p>
                  </div>
                  <p v-if="displayedLead?.createdAt" class="text-xs opacity-60">Reçu le {{ formatDate(displayedLead?.createdAt) }}</p>
                  <button v-if="!isLeadLocked && isSuperAdmin" class="btn btn-ghost btn-sm w-fit mt-1" @click="dissociateLead">Dissocier</button>
                </div>
                <div v-else-if="canSelectLead && companyLeads.length" class="flex flex-col gap-2 w-full">
                  <div class="flex items-center gap-2">
                    <UserPlusIcon class="w-4 h-4 shrink-0 opacity-60" />
                    <span class="text-xs opacity-70">Associer un lead</span>
                  </div>
                  <select
                    :model-value="selectedLeadUuid"
                    class="select select-bordered select-sm w-full"
                    @change="onLeadChange(($event.target as HTMLSelectElement).value || null)"
                  >
                    <option value="">— Aucun —</option>
                    <option v-for="l in companyLeads" :key="l.uuid" :value="l.uuid">
                      {{ l.companyName }} — {{ l.companySiret }} · {{ l.owner?.firstName }} {{ l.owner?.lastName }} ({{ l.type }})
                    </option>
                  </select>
                </div>
                <div
                  v-else-if="canSelectLead && !isLoadingLeads && !companyLeads.length"
                  class="flex flex-col items-center justify-center flex-1 py-6 text-center"
                >
                  <p class="text-sm text-base-content/50 italic">Aucun lead déclaré sur cette entreprise.</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Slot 3 : Commentaires Opérateur si pas de lead -->
          <!-- L'outer div n'a pas de hauteur intrinsèque (contenu absolument positionné)
               → seul le bloc devis détermine la hauteur de la ligne du grid. -->
          <div v-else ref="operatorCommentBlockRef" class="relative">
            <div class="absolute inset-0 rounded-lg border-2 border-[#27E8E8]/50 bg-[#27E8E8]/10 overflow-hidden">
              <OperatorCommentBlock :order-uuid="orderUuid ?? ''" />
            </div>
          </div>

        </div>
        <div v-if="isLoadingOrder" class="card bg-base-100 shadow">
          <div class="card-body">
            <span class="loading loading-spinner loading-lg"></span>
          </div>
        </div>
        <div v-else-if="selectedQuote && selectedFormType && adaptedSchema" class="card bg-base-100 shadow">
          <div class="card-body">
            <div class="flex items-start justify-between gap-4">
              <div>
                <h2 class="card-title text-lg">3) {{ adaptedSchema.name }}</h2>
                <p v-if="isChildFollowingParent" class="text-xs text-info mt-1 italic">
                  Cette commande suit la commande principale — son statut est mis à jour automatiquement, modification impossible ici.
                </p>
                <p v-else-if="isChildOrder" class="text-xs text-base-content/60 mt-1 italic">
                  Ce formulaire a été initialisé depuis la commande principale. Tous les champs restent modifiables.
                </p>
              </div>
            </div>

            <div class="mt-4">
              <DynamicForm
                :key="`${selectedFormType}-${orderUuid || 'new'}`"
                ref="dynamicFormRef"
                v-model="formData"
                :schema="adaptedSchema"
                :order-files="orderFiles"
                :fields-disabled="isFormReadOnly"
                @delete-file="onDeleteFile"
              />
            </div>

          </div>
        </div>

        <div v-else-if="!isLoadingOrder" class="card bg-base-100 shadow">
          <div class="card-body">
            <h2 class="card-title text-lg">3) Formulaire</h2>
            <p class="text-sm text-base-content/70">
              {{ isEditMode ? 'Chargement...' : 'Sélectionne un devis puis un type de formulaire pour commencer.' }}
            </p>
          </div>
        </div>

        <div v-if="(selectedQuote && selectedFormType && adaptedSchema) || isEditMode" class="card bg-base-100 shadow">
          <div class="card-body">
            <h2 class="card-title text-lg">Commentaires</h2>
            <div v-if="orderComments.length" class="space-y-4 mt-2">
              <div v-for="(c, i) in orderComments" :key="c.uuid || i" class="p-4 bg-base-200/50 rounded-lg border border-base-300">
                <div class="flex items-center justify-between gap-2 mb-2">
                  <span class="font-semibold text-sm">
                    {{ c.author?.firstName }} {{ c.author?.lastName }}
                    <span v-if="!c.author?.firstName && !c.author?.lastName">{{ c.author?.email || c.author }}</span>
                  </span>
                  <span class="text-xs text-base-content/60">
                    {{ c.createdAt || c.dateCreated || c.date ? new Date(c.createdAt || c.dateCreated || c.date).toLocaleString('fr-FR') : '' }}
                  </span>
                </div>
                <div class="text-sm prose prose-sm max-w-none" v-html="c.comment || c.content || c.message || ''"></div>
              </div>
            </div>
            <p v-else class="text-sm text-base-content/50 italic mt-2">Aucun commentaire.</p>
            <div v-if="canSalesEdit || canAdvAction || canUnvalidate || canManagerUnblock"
                 class="mt-4 p-4 bg-base-200/50 rounded-lg border border-base-300"
                 :class="{ 'ring-2 ring-error ring-offset-2 ring-offset-base-100': showMissingFieldBorders && sendAdvRequirements.hasComment === false }"
            >
              <WysiwygInput v-model="commentaireAjout" name="comment" label="Ajouter un commentaire" placeholder="Écrire un commentaire..." />
            </div>
          </div>
        </div>
      </div>

    </div>

    <!-- Barre sticky (UX) -->
    <div
      v-if="(selectedQuote && selectedFormType && adaptedSchema) || (isEditMode && !isLoadingOrder)"
      class="sticky bottom-0 border-t border-base-200 bg-base-100/95 backdrop-blur"
    >
      <div class="max-w-[1600px] mx-auto w-full p-4 flex items-center justify-between gap-4">
        <div v-if="simulateCommission" class="dropdown dropdown-top dropdown-start dropdown-hover">
          <div tabindex="0" role="button" class="btn btn-ghost btn-sm gap-2 cursor-pointer" title="Survoler pour voir le détail">
            <span class="text-sm opacity-70">Commission simulée :</span>
            <span class="font-semibold">
              {{ simulatedCommissionAmount != null ? `${simulatedCommissionAmount.toFixed(2)} €` : '-' }}
            </span>
          </div>
          <div tabindex="0" class="dropdown-content z-[100] card card-compact bg-base-100 shadow-xl w-80 p-4 mb-2">
            <div class="flex items-center justify-between mb-2">
              <h3 class="font-semibold text-sm">Détail simulation</h3>
              <button class="btn btn-ghost btn-xs btn-square" :disabled="isRefreshingSimulation" title="Actualiser" @click.stop="refreshSimulation">
                <ArrowPathIcon class="w-4 h-4" :class="{ 'animate-spin': isRefreshingSimulation }" />
              </button>
            </div>
            <table class="table table-xs">
              <tbody>
                <tr>
                  <td class="opacity-70">Marge compteur</td>
                  <td class="text-right">{{ simulatedMargeCompteur != null ? `${simulatedMargeCompteur.toFixed(2)} €` : '-' }}</td>
                </tr>
                <tr>
                  <td class="opacity-70">Taux atteint</td>
                  <td class="text-right">{{ (simulateCommission.reachedRate * 100).toFixed(2) }} %</td>
                </tr>
                <tr>
                  <td class="opacity-70">Coef paiement</td>
                  <td class="text-right">{{ simulateCommission.coefficients?.paymentCoef ?? '-' }}</td>
                </tr>
                <tr>
                  <td class="opacity-70">Coef durée</td>
                  <td class="text-right">{{ simulateCommission.coefficients?.durationCoef ?? '-' }}</td>
                </tr>
                <tr>
                  <td class="opacity-70">Coef contrat</td>
                  <td class="text-right">{{ simulateCommission.coefficients?.contractCoef ?? '-' }}</td>
                </tr>
                <tr>
                  <td class="opacity-70">Coef IAV</td>
                  <td class="text-right">{{ simulateCommission.coefficients?.iavCoef ?? '-' }}</td>
                </tr>
                <tr v-if="simulateCommission.hasLead">
                  <td class="opacity-70">Part ing. commercial</td>
                  <td class="text-right">{{ ((simulateCommission.salesEngineerShare ?? 1) * 100).toFixed(0) }} %</td>
                </tr>
                <tr v-if="simulateCommission.hasLead">
                  <td class="opacity-70">Part apporteur</td>
                  <td class="text-right">{{ ((simulateCommission.ownerShare ?? 0) * 100).toFixed(0) }} %</td>
                </tr>
                <tr class="border-t">
                  <td class="font-semibold">Commission simulée</td>
                  <td class="text-right font-bold">{{ simulatedCommissionAmount != null ? `${simulatedCommissionAmount.toFixed(2)} €` : '-' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div v-else class="flex-1" />
        <div v-if="!isProjectRead" class="flex gap-3 shrink-0">
          <OrderActionsAdv
            v-if="isAdv"
            :status="orderStatus || ''"
            :is-assigned-to-me="isAssignedToMe"
            :is-author-or-commercial="isAuthorOrCommercial"
            :can-send-to-adv="canSendToAdv"
            :hide-send-adv="isChildFollowingParent"
            :is-child-following-parent="isChildFollowingParent"
            :send-adv-block-reason="sendAdvBlockReason"
            :send-adv-requirements="sendAdvRequirements"
            :is-saving="isSaving"
            :is-updating-status="isUpdatingStatus"
            :is-claiming="isClaiming"
            :is-adv-manager="isAdvManager"
            :processing-date="processingDate"
            :is-edit-mode="isEditMode"
            :can-validate-bu-manager="canValidateBuManager"
            @save="onSave"
            @send-adv="onClickSendAdv"
            @claim="onClaimOrder"
            @block-business="onUpdateStatus(ORDER_STATUS.BLOCKED_BUSINESS)"
            @block-operator="onUpdateStatus(ORDER_STATUS.BLOCKED_OPERATOR)"
            @cancel="onClickCancel"
            @delete="onClickDelete"
            @validate="onClickValidated"
            @unvalidate="onClickUnvalidate"
            @unblock="onUpdateStatus(ORDER_STATUS.IN_PROGRESS)"
            @resend-adv="onUpdateStatus(ORDER_STATUS.IN_PROGRESS)"
            @view-parent="goToParentOrder"
            @info-hover="showMissingFieldBorders = $event"
            @open-processing-date-modal="showProcessingDateModal = true; processingDateComment = ''"
            @validate-bu-manager="onValidateBuManager"
            @refuse-bu-manager="onClickRefuseBuManager"
          />
          <OrderActionsCommercial
            v-else
            :status="orderStatus || ''"
            :is-author-or-commercial="isAuthorOrCommercial"
            :can-unblock="canManagerUnblock"
            :can-send-to-adv="canSendToAdv"
            :hide-send-adv="isChildFollowingParent"
            :send-adv-block-reason="sendAdvBlockReason"
            :send-adv-requirements="sendAdvRequirements"
            :is-saving="isSaving"
            :is-updating-status="isUpdatingStatus"
            :is-edit-mode="isEditMode"
            :can-validate-bu-manager="canValidateBuManager"
            :needs-bu-manager-validation="needsBuManagerValidation"
            @save="onSave"
            @send-adv="onClickSendAdv"
            @cancel="onClickCancel"
            @delete="onClickDelete"
            @resend-adv="onUpdateStatus(ORDER_STATUS.IN_PROGRESS)"
            @unblock="onUpdateStatus(ORDER_STATUS.IN_PROGRESS)"
            @info-hover="showMissingFieldBorders = $event"
            @validate-bu-manager="onValidateBuManager"
            @refuse-bu-manager="onClickRefuseBuManager"
          />
        </div>
      </div>
    </div>

    <ConfirmModal
      :show-modal="showModalCancel"
      title="Annuler la commande"
      confirm-text="Confirmer l'annulation"
      cancel-text="Retour"
      confirm-variant="error"
      :loading="isUpdatingStatus"
      @confirm="onConfirmCancel"
      @cancel="showModalCancel = false"
    >
      <template #body>
        <div class="alert alert-error">
          <InformationCircleIcon class="w-6 h-6 shrink-0" />
          <div>
            <p class="font-semibold">Annuler la commande</p>
            <p class="text-sm opacity-70">Cette action est irréversible. La commande passera au statut Annulé.</p>
          </div>
        </div>
      </template>
    </ConfirmModal>
    <ConfirmModal
      :show-modal="showModalDelete"
      :title="orderStore.currentOrder?.status?.toUpperCase() === ORDER_STATUS.CANCELED ? 'Supprimer la commande' : 'Supprimer le brouillon'"
      confirm-text="Supprimer définitivement"
      cancel-text="Annuler"
      confirm-variant="error"
      :loading="isUpdatingStatus"
      @confirm="onConfirmDelete"
      @cancel="showModalDelete = false"
    >
      <template #body>
        <div class="alert alert-error">
          <InformationCircleIcon class="w-6 h-6 shrink-0" />
          <div>
            <p class="font-semibold">
              {{ orderStore.currentOrder?.status?.toUpperCase() === ORDER_STATUS.CANCELED ? 'Supprimer la commande annulée' : 'Supprimer le brouillon de commande' }}
            </p>
            <p class="text-sm opacity-70">Cette action est irréversible. Toutes les données saisies seront perdues.</p>
          </div>
        </div>
      </template>
    </ConfirmModal>
    <ConfirmModal
      :show-modal="showModalValidate"
      title="Valider la commande"
      confirm-text="Confirmer la validation"
      cancel-text="Annuler"
      confirm-variant="success"
      :loading="isUpdatingStatus"
      @confirm="onConfirmValidate"
      @cancel="showModalValidate = false"
    >
      <template #body>
        <div class="alert alert-warning">
          <InformationCircleIcon class="w-6 h-6 shrink-0" />
          <div>
            <p class="font-semibold">Une fois validée, la commande ne pourra plus être modifiée</p>
            <p class="text-sm opacity-70">
              Cette action est irréversible. Les boutons de changement de statut et d'édition ne seront plus accessibles.
            </p>
          </div>
        </div>
      </template>
    </ConfirmModal>

    <ConfirmModal
      :show-modal="showModalUnvalidate"
      title="Dévalider la commande"
      confirm-text="Confirmer la dévalidation"
      cancel-text="Retour"
      confirm-variant="warning"
      :loading="isUpdatingStatus"
      @confirm="onConfirmUnvalidate"
      @cancel="showModalUnvalidate = false"
    >
      <template #body>
        <div class="alert alert-warning">
          <InformationCircleIcon class="w-6 h-6 shrink-0" />
          <div>
            <p class="font-semibold">Attention : cette action entraîne des conséquences importantes</p>
            <p class="text-sm opacity-70 mt-2">
              La commande repassera au statut « En cours ». Les commissions liées à cette validation seront supprimées. Cette action est tracée.
            </p>
          </div>
        </div>
      </template>
    </ConfirmModal>

    <ConfirmModal
      :show-modal="showModalRefuseBuManager"
      title="Refuser et renvoyer à l'IC"
      confirm-text="Confirmer le refus"
      cancel-text="Annuler"
      confirm-variant="error"
      :confirm-disabled="!refuseBuManagerComment.trim()"
      :loading="isUpdatingStatus"
      @confirm="onConfirmRefuseBuManager"
      @cancel="showModalRefuseBuManager = false"
    >
      <template #body>
        <div class="alert alert-warning">
          <InformationCircleIcon class="w-6 h-6 shrink-0" />
          <div>
            <p class="font-semibold">La commande va être renvoyée au commercial</p>
            <p class="text-sm opacity-70 mt-1">
              Elle repassera en brouillon. Le commercial sera notifié du motif et devra la corriger puis la renvoyer.
            </p>
          </div>
        </div>
        <div class="mt-3">
          <label class="text-sm font-medium block mb-1">Motif du refus (obligatoire)</label>
          <textarea
            v-model="refuseBuManagerComment"
            rows="4"
            class="textarea textarea-bordered w-full"
            placeholder="Expliquez au commercial ce qui doit être corrigé…"
          ></textarea>
        </div>
      </template>
    </ConfirmModal>

    <ConfirmModal
      :show-modal="showModalSignManually"
      title="Signature manuelle du devis"
      confirm-text="Confirmer la signature"
      cancel-text="Annuler"
      confirm-variant="warning"
      :confirm-disabled="!signManuallyDate"
      :loading="isSigningManually"
      :confirm-icon="PencilSquareIcon"
      @confirm="onSignManuallyConfirm"
      @cancel="showModalSignManually = false"
    >
      <template #body>
        <div class="alert alert-warning">
          <ShieldCheckIcon class="w-6 h-6 shrink-0" />
          <div>
            <p class="font-semibold">Signature manuelle — action tracée</p>
            <p class="text-sm opacity-70">Cette action sera enregistrée avec votre identité et horodatée.</p>
          </div>
        </div>
        <div>
          <label class="text-sm font-medium block mb-1">Date de signature</label>
          <input v-model="signManuallyDate" type="date" class="input input-bordered w-full" />
        </div>
      </template>
    </ConfirmModal>

    <ConfirmModal
      :show-modal="showModalSendAdv"
      :title="needsBuManagerValidation ? 'Envoyer au Manager' : 'Envoyer à l\'ADV'"
      confirm-text="Confirmer l'envoi"
      cancel-text="Annuler"
      confirm-variant="primary"
      :loading="isSaving"
      @confirm="onConfirmSendAdv"
      @cancel="showModalSendAdv = false"
    >
      <template #body>
        <div class="alert alert-warning">
          <InformationCircleIcon class="w-6 h-6 shrink-0" />
          <div>
            <p class="font-semibold">Êtes-vous sûr de vouloir continuer ?</p>
            <p class="text-sm opacity-70">{{ needsBuManagerValidation ? 'Cette action soumettra la commande à votre manager pour validation.' : 'Cette action soumettra la commande à l\'ADV pour traitement.' }}</p>
          </div>
        </div>
        <ul class="space-y-2 text-sm">
          <li class="flex items-start gap-2">
            <CheckCircleIcon class="w-4 h-4 text-success shrink-0 mt-0.5" />
            <span>Avez-vous vérifié et mis à jour tous les documents ?</span>
          </li>
          <li class="flex items-start gap-2">
            <CheckCircleIcon class="w-4 h-4 text-success shrink-0 mt-0.5" />
            <span>Le devis est bien signé (électroniquement ou manuellement) ?</span>
          </li>
          <li class="flex items-start gap-2">
            <CheckCircleIcon class="w-4 h-4 text-success shrink-0 mt-0.5" />
            <span>Toutes les informations client sont correctes et à jour ?</span>
          </li>
          <li class="flex items-start gap-2">
            <CheckCircleIcon class="w-4 h-4 text-success shrink-0 mt-0.5" />
            <span>Le formulaire est complètement et correctement rempli ?</span>
          </li>
        </ul>
      </template>
    </ConfirmModal>

    <ConfirmModal
      :show-modal="showModalExistingOrder"
      title="⚠️ Commande existante"
      confirm-text="Voir la commande"
      cancel-text="Fermer"
      confirm-variant="primary"
      :confirm-icon="DocumentDuplicateIcon"
      @confirm="goToExistingOrder"
      @cancel="closeModalExistingOrder"
    >
      <template #body>
        <div class="alert alert-warning">
          <InformationCircleIcon class="w-6 h-6" />
          <div>
            <p class="font-semibold">Une commande existe déjà pour ce devis</p>
            <p class="text-sm opacity-70">Référence devis : {{ selectedQuote?.quoteReference }}</p>
            <p class="text-sm opacity-70">UUID commande : {{ existingOrder?.uuid }}</p>
          </div>
        </div>
      </template>
    </ConfirmModal>

    <ConfirmModal
      :show-modal="showProcessingDateModal"
      title="Modifier la date de traitement"
      confirm-text="Valider"
      :confirm-disabled="!newProcessingDate || !processingDateComment"
      @confirm="onUpdateProcessingDate"
      @cancel="showProcessingDateModal = false"
    >
      <template #body>
        <div class="space-y-4 gap-4">
          <DateInput v-model="newProcessingDate" label="Date de traitement" required />
          <div class="form-control w-full">
            <label class="label font-semibold">
              <span class="label-text">Commentaire <span class="text-red-500">*</span></span>
            </label>
            <textarea
              v-model="processingDateComment"
              class="textarea textarea-bordered h-24 w-full"
              placeholder="Saisissez un commentaire obligatoire..."
              required
            ></textarea>
          </div>
        </div>
      </template>
    </ConfirmModal>

    <OrderChildrenModal
      v-model:selected-children="selectedChildren"
      :show-modal="showChildModal"
      :form-type-options="formTypeOptions"
      :parent-form-type="selectedFormType"
      :parent-reference="selectedQuote?.quoteReference"
      :parent-order-uuid="orderUuid"
      :is-form-read-only="!canManageChildren"
      :initial-children="initialChildrenSnapshot"
      @close="showChildModal = false"
    />

    <ConfirmModal
      :show-modal="showModalDerogationRequest"
      title="Soumettre une demande de dérogation"
      confirm-text="Soumettre"
      cancel-text="Annuler"
      confirm-variant="primary"
      :confirm-icon="PaperAirplaneIcon"
      :loading="isSubmittingDerogation"
      @confirm="onSubmitDerogationRequest"
      @cancel="showModalDerogationRequest = false; Object.keys(derogationComments).forEach((key) => delete derogationComments[key])"
    >
      <template #body>
        <div class="alert alert-info">
          <InformationCircleIcon class="w-6 h-6 shrink-0" />
          <div>
            <p class="font-semibold">Arguments commerciaux requis</p>
            <p class="text-sm opacity-70">Chaque dérogation manquante doit être justifiée par un argument commercial.</p>
          </div>
        </div>
        <div class="space-y-4">
          <div
            v-for="item in missingDerogations"
            :key="getDerogationKey(item)"
            class="rounded-lg border border-base-200 p-4 bg-base-100"
          >
            <p class="font-semibold text-sm mb-2">{{ item.description || `${item.type} (${item.level || 'Niveau inconnu'})` }}</p>
            <textarea
              v-model="derogationComments[getDerogationKey(item)]"
              class="textarea textarea-bordered h-28 w-full"
              :placeholder="`Argument commercial`"
              :disabled="isSubmittingDerogation"
            ></textarea>
            <p class="text-xs text-base-content/60 mt-1">Minimum 10 caractères</p>
          </div>
        </div>
      </template>
    </ConfirmModal>

    <DerogationDetailModal
      :derogation="selectedDerogation"
      :is-open="showDerogationDetailModal"
      @close="showDerogationDetailModal = false"
      @updated="onDerogationValidated"
    />

    <ConfirmModal
      :show-modal="showModalArbitrationRequest"
      title="Soumettre une demande d'arbitrage BU"
      confirm-text="Soumettre"
      cancel-text="Annuler"
      confirm-variant="primary"
      :confirm-icon="PaperAirplaneIcon"
      :loading="isSubmittingArbitration"
      @confirm="onSubmitArbitrationRequest"
      @cancel="showModalArbitrationRequest = false; arbitrationCommercialArgument = ''"
    >
      <template #body>
        <div class="alert alert-info">
          <InformationCircleIcon class="w-6 h-6 shrink-0" />
          <div>
            <p class="font-semibold">Justification requise</p>
            <p class="text-sm opacity-70">
              La société ne relève pas naturellement de votre BU. Justifiez pourquoi vous devez conserver ce dossier.
              Les directeurs commerciaux (BU source + BU cible) seront notifiés.
            </p>
          </div>
        </div>
        <div class="rounded-lg border border-base-200 p-4 bg-base-100">
          <p class="font-semibold text-sm mb-2">Argument commercial</p>
          <textarea
            v-model="arbitrationCommercialArgument"
            class="textarea textarea-bordered h-28 w-full"
            placeholder="Expliquez pourquoi cet arbitrage doit être accordé..."
            :disabled="isSubmittingArbitration"
          ></textarea>
          <p class="text-xs text-base-content/60 mt-1">Minimum 10 caractères</p>
        </div>
      </template>
    </ConfirmModal>

    <ArbitrationDetailModal
      :arbitration="selectedArbitration"
      :is-open="showArbitrationDetailModal"
      @close="showArbitrationDetailModal = false"
      @updated="onArbitrationDecided"
    />
  </div>
</template>
